import { historicalData } from './data.js';
import { FinancialConfig, getMinimumWithdrawalRate, getAgePensionParams, getAgePensionParamsWithPensionGrowth, calculateSGContributions, calculateAvailableConcessionalCap, calculateAvailableNonConcessionalCap, calculateDivision293Tax, calculateDivision296MemberAccumulationSurcharge, calculateLISTO, calculateLITO, calculateSeniorsTaxOffset, calculateSpouseContributionOffset, calculateSuperCGTTax, calculateMinimumPensionWithdrawal, checkWorkTestRequirements, calculateBringForwardRule, checkTSBThresholds } from './FinancialConfig.js';
import { SMSFFeeEngine } from './SMSFFeeEngine.js';

// --- Simulation Engine ---
// Refactored version with duplicate functions removed and centralized configuration
export const SimulationEngine = {
    // Superannuation Guarantee and Contribution Cap Manager
    SGManager: {
        // Calculate SG contributions for a given salary
        calculateSGContributions(annualSalary) {
            return calculateSGContributions(annualSalary);
        },
        
        // Calculate government co-contribution
        calculateCoContribution(income, personalNonConcessional, age) {
            const CO_CONTRIB = FinancialConfig.CONTRIBUTION_CAPS.CO_CONTRIBUTION;
            
            // Check age eligibility
            if (age >= CO_CONTRIB.AGE_LIMIT) {
                return {
                    amount: 0,
                    eligible: false,
                    reason: `Must be under ${CO_CONTRIB.AGE_LIMIT} years old`
                };
            }
            
            // Check if made eligible contributions
            if (personalNonConcessional <= 0) {
                return {
                    amount: 0,
                    eligible: false,
                    reason: 'No personal non-concessional contributions made'
                };
            }
            
            // Check income thresholds
            if (income > CO_CONTRIB.MAX_INCOME_THRESHOLD) {
                return {
                    amount: 0,
                    eligible: false,
                    reason: `Income exceeds threshold ($${CO_CONTRIB.MAX_INCOME_THRESHOLD.toLocaleString()})`
                };
            }
            
            let coContributionAmount = 0;
            
            if (income <= CO_CONTRIB.MIN_INCOME_THRESHOLD) {
                // Maximum co-contribution (at or below lower threshold)
                coContributionAmount = Math.min(
                    personalNonConcessional * CO_CONTRIB.MATCHING_RATE,
                    CO_CONTRIB.MAX_AMOUNT
                );
            } else {
                // Graduated co-contribution
                const incomeAboveThreshold = income - CO_CONTRIB.MIN_INCOME_THRESHOLD;
                const reductionRate = CO_CONTRIB.MAX_AMOUNT / 
                    (CO_CONTRIB.MAX_INCOME_THRESHOLD - CO_CONTRIB.MIN_INCOME_THRESHOLD);
                const maxEligible = CO_CONTRIB.MAX_AMOUNT - (incomeAboveThreshold * reductionRate);
                
                coContributionAmount = Math.min(
                    personalNonConcessional * CO_CONTRIB.MATCHING_RATE,
                    maxEligible
                );
            }
            
            return {
                amount: Math.max(0, Math.round(coContributionAmount)),
                eligible: true,
                reason: null
            };
        },

        calculateLISTO(adjustedTaxableIncome, concessionalContributions) {
            return calculateLISTO(adjustedTaxableIncome, concessionalContributions);
        },

        // Calculate available concessional contribution cap (including carry-forward)
        calculateAvailableConcessionalCap(totalSuperBalance, carryForwardBalances = []) {
            return calculateAvailableConcessionalCap(totalSuperBalance, carryForwardBalances);
        },

        // Calculate available non-concessional contribution cap
        calculateAvailableNonConcessionalCap(totalSuperBalance) {
            return calculateAvailableNonConcessionalCap(totalSuperBalance);
        },

        // Track carry-forward balances for concessional contributions
        updateCarryForwardBalances(carryForwardBalances, currentYearUsage, baseCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL) {
            const existing = Array.isArray(carryForwardBalances)
                ? carryForwardBalances.map((v) => Math.max(0, Number(v) || 0)).slice()
                : [];
            let excessUsage = Math.max(0, (Number(currentYearUsage) || 0) - baseCap);
            // Consume oldest available amounts first.
            for (let i = existing.length - 1; i >= 0 && excessUsage > 0; i--) {
                const used = Math.min(existing[i], excessUsage);
                existing[i] -= used;
                excessUsage -= used;
            }
            const unusedCap = Math.max(0, baseCap - (Number(currentYearUsage) || 0));
            const newCarryForward = [unusedCap, ...existing];
            const maxYears = FinancialConfig.CONTRIBUTION_CAPS.CARRY_FORWARD_YEARS;
            return newCarryForward.slice(0, maxYears);
        },

        // Validate contributions against caps
        validateContributions(concessionalAmount, nonConcessionalAmount, totalSuperBalance, carryForwardBalances = []) {
            const availableConcessional = calculateAvailableConcessionalCap(totalSuperBalance, carryForwardBalances);
            const availableNonConcessional = calculateAvailableNonConcessionalCap(totalSuperBalance);
            
            const concessionalExcess = Math.max(0, concessionalAmount - availableConcessional);
            const nonConcessionalExcess = Math.max(0, nonConcessionalAmount - availableNonConcessional);
            
            return {
                valid: concessionalExcess === 0 && nonConcessionalExcess === 0,
                concessionalExcess,
                nonConcessionalExcess,
                availableConcessional,
                availableNonConcessional,
                totalExcess: concessionalExcess + nonConcessionalExcess
            };
        },

        // Calculate total annual contributions including SG
        calculateTotalAnnualContributions(annualSalary, voluntaryConcessional = 0, nonConcessional = 0) {
            const sgContributions = calculateSGContributions(annualSalary);
            const totalConcessional = sgContributions + voluntaryConcessional;
            
            return {
                sgContributions,
                voluntaryConcessional,
                totalConcessional,
                nonConcessional,
                totalContributions: totalConcessional + nonConcessional
            };
        },

        /** Cap contributions to legislative limits for accumulation simulation (excess is not credited). */
        capContributionsForSimulation(concessionalAmount, nonConcessionalAmount, totalSuperBalance, age, options = {}) {
            const {
                workTestMet = true,
                enableWorkTest = true,
                carryForwardBalances = [],
            } = options;
            const effectiveWorkTestMet = enableWorkTest === false ? true : workTestMet;

            let cc = Math.max(0, Number(concessionalAmount) || 0);
            let ncc = Math.max(0, Number(nonConcessionalAmount) || 0);

            if (cc > 0) {
                const wt = checkWorkTestRequirements(age, 'concessional', effectiveWorkTestMet);
                if (!wt.passed) {
                    cc = 0;
                } else {
                    const tsbCheck = checkTSBThresholds(totalSuperBalance);
                    const maxCc = tsbCheck.carryForwardAvailable
                        ? calculateAvailableConcessionalCap(totalSuperBalance, carryForwardBalances)
                        : FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
                    cc = Math.min(cc, Math.max(0, maxCc));
                }
            }

            if (ncc > 0) {
                const wt = checkWorkTestRequirements(age, 'non-concessional', effectiveWorkTestMet);
                if (!wt.passed) {
                    ncc = 0;
                } else {
                    const tsbCheck = checkTSBThresholds(totalSuperBalance);
                    if (!tsbCheck.nonConcessionalContributionsAllowed) {
                        ncc = 0;
                    } else {
                        const annualMax = calculateAvailableNonConcessionalCap(totalSuperBalance);
                        if (ncc > annualMax) {
                            const bf = calculateBringForwardRule(totalSuperBalance, ncc);
                            ncc = Math.min(ncc, Math.max(0, bf.applicable ? bf.maximumContribution : annualMax));
                        }
                    }
                }
            }

            return { concessional: cc, nonConcessional: ncc };
        },

        createAnnualCapState(carryForwardBalances = []) {
            return {
                carryForwardBalances: Array.isArray(carryForwardBalances)
                    ? carryForwardBalances.map((v) => Math.max(0, Number(v) || 0)).slice(0, 5)
                    : [],
                bringForwardYearsRemaining: 0,
                bringForwardRemaining: 0,
            };
        },

        /**
         * Apply one recipient's combined annual CC/NCC limits and advance cap state.
         * SG, salary sacrifice and scheduled CCs must be aggregated before this call.
         * Own, spouse and scheduled NCCs must likewise be aggregated per recipient.
         */
        capAnnualContributions({
            concessional = 0,
            nonConcessional = 0,
            totalSuperBalance = 0,
            age = 0,
            state = null,
            workTestMet = true,
            enableWorkTest = true,
        } = {}) {
            const capState = state || SimulationEngine.SGManager.createAnnualCapState();
            const ccIntent = Math.max(0, Number(concessional) || 0);
            const nccIntent = Math.max(0, Number(nonConcessional) || 0);
            const ageNum = Number(age) || 0;

            let allowedCc = 0;
            const ccAgeCheck = checkWorkTestRequirements(ageNum, 'concessional', workTestMet);
            if (ccAgeCheck.passed && ccIntent > 0) {
                const tsbCheck = checkTSBThresholds(totalSuperBalance);
                const maxCc = tsbCheck.carryForwardAvailable
                    ? calculateAvailableConcessionalCap(totalSuperBalance, capState.carryForwardBalances)
                    : FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
                allowedCc = Math.min(ccIntent, Math.max(0, maxCc));
            }

            const baseCcCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
            capState.carryForwardBalances = SimulationEngine.SGManager.updateCarryForwardBalances(
                capState.carryForwardBalances,
                allowedCc,
                baseCcCap
            );

            let allowedNcc = 0;
            const nccAgeCheck = checkWorkTestRequirements(ageNum, 'nonConcessional', workTestMet);
            if (nccAgeCheck.passed && nccIntent > 0) {
                const tsbCheck = checkTSBThresholds(totalSuperBalance);
                if (tsbCheck.nonConcessionalContributionsAllowed) {
                    if (capState.bringForwardYearsRemaining > 0) {
                        allowedNcc = Math.min(nccIntent, Math.max(0, capState.bringForwardRemaining));
                        capState.bringForwardRemaining = Math.max(0, capState.bringForwardRemaining - allowedNcc);
                    } else {
                        const annualMax = calculateAvailableNonConcessionalCap(totalSuperBalance);
                        if (nccIntent > annualMax) {
                            const bf = calculateBringForwardRule(totalSuperBalance, nccIntent);
                            const maximum = Math.max(0, bf.applicable ? bf.maximumContribution : annualMax);
                            allowedNcc = Math.min(nccIntent, maximum);
                            if (bf.applicable && bf.yearsToUse > 1) {
                                capState.bringForwardYearsRemaining = bf.yearsToUse;
                                capState.bringForwardRemaining = Math.max(0, maximum - allowedNcc);
                            }
                        } else {
                            allowedNcc = Math.min(nccIntent, Math.max(0, annualMax));
                        }
                    }
                }
            }

            if (capState.bringForwardYearsRemaining > 0) {
                capState.bringForwardYearsRemaining -= 1;
                if (capState.bringForwardYearsRemaining === 0) {
                    capState.bringForwardRemaining = 0;
                }
            }

            return { concessional: allowedCc, nonConcessional: allowedNcc, state: capState };
        },

        /**
         * Amount credited after 15% contributions tax, plus Division 293 when income + concessional
         * contributions exceed the threshold (modelled as paid from super). Caps are tested on the gross.
         */
        netOfContributionsTax(concessionalAmount, taxableIncome = 0) {
            const gross = Math.max(0, Number(concessionalAmount) || 0);
            if (gross === 0) return 0;
            const baseTax = gross * FinancialConfig.SUPER_TAX_RATES.CONTRIBUTIONS_TAX;
            const div293 = calculateDivision293Tax(taxableIncome, gross);
            return gross - baseTax - div293;
        }
    },

    // Tax Calculation Manager
    TaxManager: {
        // Calculate Division 293 tax for high-income earners
        calculateDivision293Tax(totalIncome, concessionalContributions) {
            return calculateDivision293Tax(totalIncome, concessionalContributions);
        },

        // Calculate Low Income Tax Offset (LITO)
        calculateLITO(taxableIncome) {
            return calculateLITO(taxableIncome);
        },

        // Calculate Seniors Tax Offset
        calculateSeniorsTaxOffset(taxableIncome, isCouple = false, age = 65) {
            return calculateSeniorsTaxOffset(taxableIncome, isCouple, age);
        },

        // Calculate Spouse Contribution Tax Offset
        calculateSpouseContributionOffset(contributionAmount, spouseIncome) {
            return calculateSpouseContributionOffset(contributionAmount, spouseIncome);
        },

        // Calculate CGT tax for superannuation context
        calculateSuperCGTTax(capitalGain, isLongTerm = true) {
            return calculateSuperCGTTax(capitalGain, isLongTerm);
        },

        // Calculate total tax offsets applicable to a taxpayer
        calculateTotalTaxOffsets(taxableIncome, isCouple = false, age = 65, spouseIncome = 0, spouseContribution = 0) {
            let totalOffsets = 0;

            // LITO applies to all taxpayers
            totalOffsets += calculateLITO(taxableIncome);

            // Seniors offset applies to those 65+
            totalOffsets += calculateSeniorsTaxOffset(taxableIncome, isCouple, age);

            // Spouse contribution offset if applicable
            if (spouseContribution > 0 && spouseIncome > 0) {
                totalOffsets += calculateSpouseContributionOffset(spouseContribution, spouseIncome);
            }

            return totalOffsets;
        },

        // Calculate effective tax rate including all offsets and Division 293
        calculateEffectiveTaxRate(taxableIncome, concessionalContributions = 0, isCouple = false, age = 65, spouseIncome = 0, spouseContribution = 0) {
            // Base tax calculation using tax brackets from FinancialConfig (loaded from JSON)
            let baseTax = 0;
            const taxBrackets = FinancialConfig.INCOME_TAX_BRACKETS;

            for (const bracket of taxBrackets) {
                if (taxableIncome > bracket.min) {
                    const taxableInBracket = Math.min(taxableIncome, bracket.max) - bracket.min;
                    baseTax += taxableInBracket * bracket.rate;
                }
            }

            // Add Division 293 tax if applicable
            const div293Tax = calculateDivision293Tax(taxableIncome, concessionalContributions);

            // Calculate total offsets
            const totalOffsets = SimulationEngine.TaxManager.calculateTotalTaxOffsets(taxableIncome, isCouple, age, spouseIncome, spouseContribution);

            // Calculate net tax
            const netTax = Math.max(0, baseTax + div293Tax - totalOffsets);

            // Return effective rate
            return taxableIncome > 0 ? netTax / taxableIncome : 0;
        }
    },

    // Superannuation Compliance Manager
    ComplianceManager: {
        // Check minimum pension withdrawal requirements
        checkMinimumPensionWithdrawal(pensionBalance, age) {
            return calculateMinimumPensionWithdrawal(pensionBalance, age);
        },

        // Validate work test requirements for contributions
        validateWorkTest(age, contributionType = 'concessional', workTestMet = false) {
            return checkWorkTestRequirements(age, contributionType, workTestMet);
        },

        // Calculate bring-forward rule limits
        calculateBringForwardLimits(totalSuperBalance, proposedContribution) {
            return calculateBringForwardRule(totalSuperBalance, proposedContribution);
        },

        // Check total super balance thresholds
        checkTotalSuperBalanceThresholds(totalSuperBalance) {
            return checkTSBThresholds(totalSuperBalance);
        },

        // Comprehensive contribution validation
        validateContributionsWithCompliance(concessionalAmount, nonConcessionalAmount, totalSuperBalance, age, workTestMet = false, carryForwardBalances = []) {
            const results = {
                valid: true,
                errors: [],
                warnings: [],
                compliance: {}
            };

            // Check TSB thresholds
            const tsbCheck = checkTSBThresholds(totalSuperBalance);
            results.compliance.tsbThresholds = tsbCheck;

            // Validate concessional contributions
            if (concessionalAmount > 0) {
                const workTestResult = checkWorkTestRequirements(age, 'concessional', workTestMet);
                results.compliance.concessionalWorkTest = workTestResult;

                if (!workTestResult.passed) {
                    results.valid = false;
                    results.errors.push(`Concessional contributions not allowed: ${workTestResult.reason}`);
                }

                // Check carry-forward availability
                if (!tsbCheck.carryForwardAvailable) {
                    const availableCap = FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL;
                    if (concessionalAmount > availableCap) {
                        results.valid = false;
                        results.errors.push(`Concessional contributions exceed annual cap: $${availableCap.toLocaleString()}`);
                    }
                }
            }

            // Validate non-concessional contributions
            if (nonConcessionalAmount > 0) {
                const workTestResult = checkWorkTestRequirements(age, 'non-concessional', workTestMet);
                results.compliance.nonConcessionalWorkTest = workTestResult;

                if (!workTestResult.passed) {
                    results.valid = false;
                    results.errors.push(`Non-concessional contributions not allowed: ${workTestResult.reason}`);
                }

                // Check TSB cutoff
                if (!tsbCheck.nonConcessionalContributionsAllowed) {
                    results.valid = false;
                    results.errors.push(`Non-concessional contributions not allowed: TSB exceeds $${tsbCheck.thresholds.nonConcessionalCutoff.toLocaleString()}`);
                } else {
                    // Check bring-forward rule
                    const bringForwardResult = calculateBringForwardRule(totalSuperBalance, nonConcessionalAmount);
                    results.compliance.bringForwardRule = bringForwardResult;

                    if (bringForwardResult.applicable && nonConcessionalAmount > bringForwardResult.maximumContribution) {
                        results.valid = false;
                        results.errors.push(`Non-concessional contributions exceed bring-forward limit: $${bringForwardResult.maximumContribution.toLocaleString()}`);
                    }
                }
            }

            return results;
        },

        // Calculate minimum pension requirements for retirement planning
        calculatePensionRequirements(retirementAge, pensionBalance, lifeExpectancy = 95) {
            const requirements = [];
            
            for (let age = retirementAge; age <= lifeExpectancy; age++) {
                const minimumWithdrawal = calculateMinimumPensionWithdrawal(pensionBalance, age);
                requirements.push({
                    age,
                    minimumWithdrawal,
                    withdrawalRate: FinancialConfig.MINIMUM_PENSION_STANDARDS.WITHDRAWAL_RATES[Math.min(age, 95)]
                });
            }

            return requirements;
        }
    },

    // Fee Calculation Manager
    FeeManager: {
        // Centralized Australian Super fee calculation
        calculateAustralianSuperFees(balance, flatAdminFee = 52, adminFeePercentage = 0.10, applyAdminCap = true) {
            // AustralianSuper's actual fee structure:
            // - Admin fees: $52 + 0.10% of balance (capped at $350 when using defaults)
            // - Investment fees: 0.49% of balance (capped at $350)
            // - Transaction costs: 0.08% of balance
            
            let adminFeeAmount = flatAdminFee + (balance * adminFeePercentage / 100);
            
            // Only apply admin fee cap when using AustralianSuper defaults
            if (applyAdminCap && flatAdminFee === 52 && adminFeePercentage === 0.10) {
                adminFeeAmount = Math.min(adminFeeAmount, 350);
            }
            
            // Investment fees: 0.49% of balance (capped at $350)
            const investmentFeeAmount = Math.min(balance * 0.0049, 350);
            
            // Transaction costs: 0.08% of balance
            const transactionCostAmount = balance * 0.0008;
            
            return adminFeeAmount + investmentFeeAmount + transactionCostAmount;
        },

        /**
         * Member fees used by accumulation and Advanced/Adviser retirement engines.
         * Blank inputs fall back to AustralianSuper-style defaults; an explicit 0 is respected.
         * Admin capped at $350 only on the $52 + 0.10% default; 0.08% transaction costs unless every fee is 0.
         */
        calculateMemberFees(balance, adminFees, adminFeePercentage, investmentFees, insuranceFees = 0, investmentFeeCap = null) {
            const num = (v, fallback) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? fallback : Number(v));
            const flat = num(adminFees, 52);
            const adminPct = num(adminFeePercentage, 0.10);
            const investPct = num(investmentFees, 0);
            const insurance = num(insuranceFees, 0);

            let admin = flat + (balance * adminPct / 100);
            if (flat === 52 && adminPct === 0.10) admin = Math.min(admin, 350);

            let investment = balance * (investPct / 100);
            const cap = Number(investmentFeeCap);
            if (Number.isFinite(cap) && cap > 0) investment = Math.min(investment, cap);

            const allZero = flat === 0 && adminPct === 0 && investPct === 0 && insurance === 0;
            const transaction = allZero ? 0 : balance * 0.0008;
            return admin + investment + transaction + insurance;
        },

        /**
         * Household fees when each member holds their own account: flat admin, admin-% cap and
         * investment-fee cap apply per account; the household insurance premium is split evenly.
         * `memberShares` are each member's fraction of `balance` (couples default to 50/50).
         */
        calculateHouseholdFees(balance, { adminFees, adminFeePercentage, investmentFees, insuranceFees, investmentFeeCap = null } = {}, isCouple = false, memberShares = null) {
            if (!(balance > 0)) return 0;
            const insurance = Number(insuranceFees) || 0;
            if (!isCouple) {
                return SimulationEngine.FeeManager.calculateMemberFees(balance, adminFees, adminFeePercentage, investmentFees, insurance, investmentFeeCap);
            }
            const raw = Array.isArray(memberShares) ? memberShares.map((s) => Math.max(0, Number(s) || 0)) : [];
            const total = raw.reduce((a, b) => a + b, 0);
            const shares = raw.length === 2 && total > 0 ? raw.map((s) => s / total) : [0.5, 0.5];
            return shares.reduce((sum, share) => (share > 0
                ? sum + SimulationEngine.FeeManager.calculateMemberFees(balance * share, adminFees, adminFeePercentage, investmentFees, insurance / 2, investmentFeeCap)
                : sum), 0);
        },

        // Calculate total fees including additional investment fees
        calculateTotalFees(balance, adminFees = 52, adminFeePercentage = 0.10, additionalInvestmentFees = 0, applyAdminCap = true) {
            const baseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(balance, adminFees, adminFeePercentage, applyAdminCap);
            const additionalFees = balance * (additionalInvestmentFees / 100);
            return baseFees + additionalFees;
        },

        // SMSF Fee Integration - Calculate SMSF fees using SMSFFeeEngine
        calculateSMSFFees(smsfData) {
            return SMSFFeeEngine.TotalCostCalculator.calculateTotalSMSFCosts(smsfData);
        },

        // Compare SMSF vs Industry Super fees
        compareSMSFvsIndustrySuper(smsfData, industrySuperBalance = null) {
            const smsfFees = SMSFFeeEngine.TotalCostCalculator.calculateTotalSMSFCosts(smsfData);
            const balance = smsfData.balance;
            
            // Calculate equivalent industry super fees
            const industrySuperFees = industrySuperBalance ? 
                SimulationEngine.FeeManager.calculateTotalFees(industrySuperBalance) :
                SimulationEngine.FeeManager.calculateTotalFees(balance);
            
            return {
                smsfFees: smsfFees.totalAnnualCosts,
                industrySuperFees,
                feeDifference: smsfFees.totalAnnualCosts - industrySuperFees,
                smsfFeePercentage: smsfFees.costAsPercentage,
                industrySuperFeePercentage: (industrySuperFees / balance * 100).toFixed(3),
                recommendation: smsfFees.totalAnnualCosts < industrySuperFees ? 'SMSF' : 'Industry Super',
                breakEvenBalance: this.calculateSMSFBreakEvenBalance(smsfData, industrySuperFees),
                smsfBreakdown: smsfFees.breakdown
            };
        },

        // Calculate break-even balance for SMSF vs Industry Super
        calculateSMSFBreakEvenBalance(smsfData, targetIndustrySuperFees) {
            // This is a simplified calculation - in practice would need iterative approach
            const smsfFixedCosts = smsfData.auditCosts + smsfData.complianceCosts + smsfData.valuationCosts;
            const smsfVariableRate = smsfData.trusteeFeePercentage + smsfData.investmentFeePercentage;
            const industrySuperRate = 0.0057; // Approximate industry super rate
            
            if (smsfVariableRate >= industrySuperRate) {
                return null; // SMSF will never break even
            }
            
            return smsfFixedCosts / (industrySuperRate - smsfVariableRate);
        },

        // SMSF Fee Projection over time
        projectSMSFFeesOverTime(smsfData, years = 10, growthRate = 0.05, inflationRate = 0.03) {
            const projections = [];
            let balance = smsfData.balance;
            
            for (let year = 0; year <= years; year++) {
                const currentSmsfData = { ...smsfData, balance };
                const smsfFees = SMSFFeeEngine.TotalCostCalculator.calculateTotalSMSFCosts(currentSmsfData);
                const industrySuperFees = SimulationEngine.FeeManager.calculateTotalFees(balance);
                
                projections.push({
                    year,
                    balance: Math.round(balance),
                    smsfFees: smsfFees.totalAnnualCosts,
                    industrySuperFees,
                    feeDifference: smsfFees.totalAnnualCosts - industrySuperFees,
                    cumulativeSavings: projections.reduce((sum, p) => sum + p.feeDifference, 0)
                });
                
                balance = balance * (1 + growthRate);
                
                // Apply inflation to fixed costs
                smsfData.auditCosts *= (1 + inflationRate);
                smsfData.complianceCosts *= (1 + inflationRate);
                smsfData.valuationCosts *= (1 + inflationRate);
            }
            
            return projections;
        }
    },

    // What-If Scenario Manager
    WhatIfManager: {
        // Centralized maximum sustainable income calculation
        calculateMaxSustainableIncomeWithMainCalculatorLogic(startYear, years, totalSuper, partnerSuper, weights, strategy, customInvestmentFees = null, tolerance = 1000, scenarioType = null) {
            // Get historical data and calculate returns (same as main calculator)
            let sumR = 0;
            const returnsByYear = [];
            
            for (let i = 0; i < years; i++) {
                const year = startYear + i;
                const yearData = historicalData[year];
                if (!yearData) {
                    // No historical data found
                    return 0;
                }
                
                const portfolio = { 
                    sp500: weights.sp500, 
                    ausShares: weights.aus, 
                    intlShares: weights.intl, 
                    bonds: weights.bonds, 
                    cash: weights.cash, 
                    frankingCredits: weights.frank 
                };
                
                const nominalR = Number(SimulationEngine.getPortfolioReturn(yearData, portfolio)) || 0;
                const histInfl = (historicalData[year]?.inflation) || 0;
                const investFee = (customInvestmentFees !== null ? customInvestmentFees : 0.005) / 100; // Default 0.5%
                const netR = ((1 + nominalR) / (1 + histInfl)) - 1 - investFee;
                sumR += netR;
                returnsByYear.push({ year, r: netR });
            }

            // Use binary search to find maximum sustainable income
            const averageReturn = sumR / years;
            const estimatedMaxIncome = totalSuper * (averageReturn + 0.02); // Conservative estimate
            
            let low = 0;
            let high = estimatedMaxIncome * 2;
            let best = 0;
            const maxIterations = 50;
            
            for (let i = 0; i < maxIterations && (high - low) > tolerance; i++) {
                const mid = (low + high) / 2;
                const isSustainable = SimulationEngine.isIncomeSustainable(mid, totalSuper, partnerSuper, returnsByYear, strategy, customInvestmentFees);
                
                if (isSustainable) {
                    best = mid;
                    low = mid;
                } else {
                    high = mid;
                }
            }
            
            return Math.round(best);
        },

        // Check if income is sustainable over the given period
        isIncomeSustainable(targetIncome, totalSuper, partnerSuper, returnsByYear, strategy, customInvestmentFees = null) {
            let balance = totalSuper;
            let partnerBalance = partnerSuper || 0;
            
            for (const yearData of returnsByYear) {
                const { r: returnRate } = yearData;
                
                // Apply returns
                balance *= (1 + returnRate);
                if (partnerBalance > 0) {
                    partnerBalance *= (1 + returnRate);
                }
                
                // Apply fees
                const adminFees = 52;
                const adminFeePercentage = 0.10;
                const investmentFees = customInvestmentFees !== null ? customInvestmentFees : 0.5;
                
                const baseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(balance, adminFees, adminFeePercentage);
                const additionalInvestmentFees = balance * (investmentFees / 100);
                const totalFees = baseFees + additionalInvestmentFees;
                balance = Math.max(0, balance - totalFees);
                
                if (partnerBalance > 0) {
                    const partnerBaseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(partnerBalance, adminFees, adminFeePercentage);
                    const partnerAdditionalFees = partnerBalance * (investmentFees / 100);
                    const partnerTotalFees = partnerBaseFees + partnerAdditionalFees;
                    partnerBalance = Math.max(0, partnerBalance - partnerTotalFees);
                }
                
                // Calculate withdrawal based on strategy
                const totalBalance = balance + partnerBalance;
                let withdrawal = 0;
                
                switch (strategy) {
                    case 'Fixed':
                        withdrawal = targetIncome;
                        break;
                    case 'DynamicSWR':
                        // Simplified Dynamic SWR: adjust based on portfolio performance
                        // This is a simplified version for the isIncomeSustainable function
                        const baseRate = 0.04; // 4% base rate
                        const performanceAdjustment = Math.max(0.5, Math.min(1.5, totalBalance / (targetIncome * 25))); // Adjust based on balance vs 25x income
                        withdrawal = totalBalance * baseRate * performanceAdjustment;
                        break;
                    default:
                        withdrawal = targetIncome;
                }
                
                // Apply withdrawal
                if (withdrawal > balance) {
                    if (partnerBalance > 0) {
                        const remainingWithdrawal = withdrawal - balance;
                        balance = 0;
                        partnerBalance = Math.max(0, partnerBalance - remainingWithdrawal);
                    } else {
                        return false; // Insufficient funds
                    }
                } else {
                    balance -= withdrawal;
                }
                
                // Check if we've run out of money
                if (balance <= 0 && partnerBalance <= 0) {
                    return false;
                }
            }
            
            return true;
        },

           // Target Final Balance calculation (single source of truth)
           calculateIncomeForTargetFinalBalance(startYear, inputs, withdrawalLogic, targetFinalBalance) {
               // EXACT same methodology as the other AI's fix - simple binary search
               let low = 0;
               let high = (inputs.husbandSuper + (inputs.wifeSuper || 0)) + 100000; // Generous upper bound
               let best = 0;
               const tolerance = 100; // Find income to within $100
            
            let iterations = 0;
            while (high - low > tolerance && iterations < 50) { // Prevent infinite loops
                iterations++;
                const testIncome = (low + high) / 2;
                
                // Create test inputs with the test income
                const testInputs = {
                    ...inputs,
                    fixedIncome: testIncome,
                    finalBalance: targetFinalBalance
                };
                
                // Run simulation in test mode
                const result = SimulationEngine.runSimulation(startYear, testInputs, withdrawalLogic, true);
                
                if (result.success && result.finalBalance >= targetFinalBalance) {
                    // This income worked. It's our new best guess.
                    // Let's try for an even HIGHER income.
                    best = testIncome;
                    low = testIncome;
                } else {
                    // This income failed. We must try a LOWER income.
                    high = testIncome;
                }
            }
            
            return best; // Return the highest income that succeeded
        },

        // Calculate maximum sustainable income (for normal mode)
        calculateMaxSustainableIncome(startYear, inputs, withdrawalLogic) {
            // EXACT same methodology as the other AI's fix - simple binary search
            let low = 0;
            let high = (inputs.husbandSuper + (inputs.wifeSuper || 0)) + 100000; // Generous upper bound
            let best = 0;
            const tolerance = 100; // Find income to within $100
            
            let iterations = 0;
            while (high - low > tolerance && iterations < 50) { // Prevent infinite loops
                iterations++;
                const testIncome = (low + high) / 2;
                
                // Create test inputs with the test income
                const testInputs = {
                    ...inputs,
                    fixedIncome: testIncome,
                    finalBalance: 0 // We want to find the max income that doesn't run out
                };
                
                // Run simulation in test mode
                const result = SimulationEngine.runSimulation(startYear, testInputs, withdrawalLogic, true);
                
                if (result.success) {
                    // This income worked. It's our new best guess.
                    // Let's try for an even HIGHER income.
                    best = testIncome;
                    low = testIncome;
                } else {
                    // This income failed. We must try a LOWER income.
                    high = testIncome;
                }
            }
            
            return best; // Return the highest income that succeeded
        }
    },

    // SuperannuationManager to handle TBC separation
    SuperannuationManager: {
        initializeAccounts(husbandSuper, wifeSuper, isCouple, generalTBC, husbandTbcUsed, wifeTbcUsed) {
            const husbandPersonalTBC = Math.max(0, generalTBC - husbandTbcUsed);
            const husbandRetire = Math.min(husbandSuper, husbandPersonalTBC);
            const husbandAccum = husbandSuper - husbandRetire;
            
            let wifeRetire = 0, wifeAccum = 0;
            if (isCouple) {
                const wifePersonalTBC = Math.max(0, generalTBC - wifeTbcUsed);
                wifeRetire = Math.min(wifeSuper, wifePersonalTBC);
                wifeAccum = wifeSuper - wifeRetire;
            }
            
            return {
                husband: { retire: husbandRetire, accum: husbandAccum },
                wife: { retire: wifeRetire, accum: wifeAccum }
            };
        },
        
        performWithdrawal(accounts, totalWithdrawal, husbandProportion) {
            let actualWithdrawn = 0;
            const wifeProportion = 1 - husbandProportion;
            const husbandWithdrawalTarget = totalWithdrawal * husbandProportion;
            const wifeWithdrawalTarget = totalWithdrawal * wifeProportion;
            
            // Withdraw from husband's accounts
            const husbandRetireAvailable = accounts.husband.retire;
            const husbandAccumAvailable = accounts.husband.accum;
            const husbandTotalAvailable = husbandRetireAvailable + husbandAccumAvailable;
            
            if (husbandTotalAvailable > 0) {
                const husbandActualWithdrawal = Math.min(husbandWithdrawalTarget, husbandTotalAvailable);
                actualWithdrawn += husbandActualWithdrawal;
                
                // Withdraw proportionally from retirement and accumulation accounts
                if (husbandActualWithdrawal > 0) {
                    const retireProportion = husbandRetireAvailable / husbandTotalAvailable;
                    const accumProportion = husbandAccumAvailable / husbandTotalAvailable;
                    
                    accounts.husband.retire -= husbandActualWithdrawal * retireProportion;
                    accounts.husband.accum -= husbandActualWithdrawal * accumProportion;
                }
            }
            
            // Withdraw from wife's accounts (if couple)
            if (accounts.wife && (accounts.wife.retire > 0 || accounts.wife.accum > 0)) {
                const wifeRetireAvailable = accounts.wife.retire;
                const wifeAccumAvailable = accounts.wife.accum;
                const wifeTotalAvailable = wifeRetireAvailable + wifeAccumAvailable;
                
                if (wifeTotalAvailable > 0) {
                    const wifeActualWithdrawal = Math.min(wifeWithdrawalTarget, wifeTotalAvailable);
                    actualWithdrawn += wifeActualWithdrawal;
                    
                    // Withdraw proportionally from retirement and accumulation accounts
                    if (wifeActualWithdrawal > 0) {
                        const retireProportion = wifeRetireAvailable / wifeTotalAvailable;
                        const accumProportion = wifeAccumAvailable / wifeTotalAvailable;
                        
                        accounts.wife.retire -= wifeActualWithdrawal * retireProportion;
                        accounts.wife.accum -= wifeActualWithdrawal * accumProportion;
                    }
                }
            }
            
            return actualWithdrawn;
        },
        
        getTotalBalance(accounts) {
            return accounts.husband.retire + accounts.husband.accum + 
                   (accounts.wife ? (accounts.wife.retire + accounts.wife.accum) : 0);
        }
    },

    // Calculates tax on earnings within the accumulation phase.
    // Optional third parameter `tsbEnd` (total super balance at year end):
    //   when provided and > $3M, adds the Division 296 additional 15% on earnings
    //   proportional to the balance above the threshold.
    memberTsbAtYearEnd(accounts, memberKey, retireEarnings = 0, accumEarnings = 0) {
        const member = memberKey === 'wife' ? accounts.wife : accounts.husband;
        if (!member) return null;
        return (member.retire || 0) + (member.accum || 0) + retireEarnings + accumEarnings;
    },

    calculateAccumulationTax(grossEarnings, portfolio, tsbEnd = null, taxOptions = {}) {
        if (grossEarnings <= 0) return 0;
        const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
        const sp500 = num(portfolio?.sp500);
        const ausShares = num(portfolio?.ausShares);
        const intlShares = num(portfolio?.intlShares);
        const bonds = num(portfolio?.bonds);
        const cash = num(portfolio?.cash);
        const defaults = SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS;
        const portfolioTurnover = Number.isFinite(Number(portfolio?.portfolioTurnover)) ? Number(portfolio.portfolioTurnover) : defaults.portfolioTurnover;
        const dividendPortion = Number.isFinite(Number(portfolio?.dividendPortion)) ? Number(portfolio.dividendPortion) : defaults.dividendPortion;
        const totalAllocation = sp500 + ausShares + intlShares + bonds + cash;
        const d296Surcharge = () => SimulationEngine._division296Tax(grossEarnings, tsbEnd, taxOptions);
        if (totalAllocation === 0) {
            const standardTax = grossEarnings * FinancialConfig.SUPER_TAX_RATES.ACCUMULATION_EARNINGS;
            return standardTax + d296Surcharge();
        }

        // Calculate earnings by asset class
        const ausSharesEarnings = grossEarnings * (ausShares / totalAllocation);
        const nonAusEquityEarnings = grossEarnings * ((sp500 + intlShares) / totalAllocation);
        const fixedIncomeEarnings = grossEarnings * ((bonds + cash) / totalAllocation);

        // Calculate dividend vs capital growth for Australian shares
        const dividendPortionOfAusReturn = dividendPortion / 100;
        const dividendEarnings = ausSharesEarnings * dividendPortionOfAusReturn;
        const ausCapitalGrowth = ausSharesEarnings * (1 - dividendPortionOfAusReturn);

        // Calculate total capital growth earnings
        const totalCapitalGrowthEarnings = ausCapitalGrowth + nonAusEquityEarnings;

        // Apply portfolio turnover to determine realized capital gains
        const turnoverRate = portfolioTurnover / 100;
        const realizedCapitalGains = totalCapitalGrowthEarnings * turnoverRate;

        // Calculate tax on different income types
        const totalIncomeForTax = dividendEarnings + fixedIncomeEarnings;
        const incomeTax = totalIncomeForTax > 0 ? totalIncomeForTax * FinancialConfig.SUPER_TAX_RATES.ACCUMULATION_EARNINGS : 0;
        const capitalGainsTax = realizedCapitalGains > 0 ? realizedCapitalGains * FinancialConfig.SUPER_TAX_RATES.CGT_DISCOUNT_RATE : 0;

        const standardTax = incomeTax + capitalGainsTax;

        // Division 296: delegates to FinancialConfig (same path as SMSF / widget calculators).
        // Member-level earnings attribution from simulation gross earnings is a planning proxy;
        // prefer reported Division 296 fund earnings when available (regs/ATO guidance still settling).
        return standardTax + d296Surcharge();
    },

    /** Default realised-turnover and Aus-dividend share for accumulation earnings tax (percent units). */
    get ACCUMULATION_TAX_ASSUMPTIONS() {
        return {
            portfolioTurnover: FinancialConfig.PORTFOLIO.DEFAULT_PORTFOLIO_TURNOVER * 100,
            dividendPortion: FinancialConfig.PORTFOLIO.DEFAULT_DIVIDEND_PORTION * 100,
        };
    },

    /** Accumulation-phase earnings tax for one member-year; blank inputs fall back to defaults. */
    accumulationEarningsTaxForInputs(grossEarnings, inputs = {}, tsbEnd = null) {
        if (!(grossEarnings > 0)) return 0;
        const pct = (v, fallback) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? fallback : Number(v));
        const defaults = SimulationEngine.ACCUMULATION_TAX_ASSUMPTIONS;
        return SimulationEngine.calculateAccumulationTax(grossEarnings, {
            sp500: pct(inputs.sp500, 35),
            ausShares: pct(inputs.ausShares, 15),
            intlShares: pct(inputs.intlShares, 20),
            bonds: pct(inputs.bonds, 20),
            cash: pct(inputs.cash, 10),
            portfolioTurnover: pct(inputs.portfolioTurnover, defaults.portfolioTurnover),
            dividendPortion: pct(inputs.dividendPortion, defaults.dividendPortion),
        }, tsbEnd);
    },

    /**
     * Apply nominal earnings tax, then deflate the complete nominal result back to real dollars.
     * Tax law applies to nominal taxable income, not the real return after inflation.
     */
    applyAccumulationReturnWithTax(balance, nominalReturn, inflation, inputs = {}, tsbEndReal = null) {
        const opening = Math.max(0, Number(balance) || 0);
        const nominal = Number(nominalReturn) || 0;
        const infl = Number(inflation) || 0;
        const nominalEarnings = opening * nominal;
        const nominalEndBeforeTax = opening + nominalEarnings;
        const nominalTax = SimulationEngine.accumulationEarningsTaxForInputs(
            nominalEarnings,
            inputs,
            tsbEndReal == null ? nominalEndBeforeTax / (1 + infl) : tsbEndReal
        );
        return Math.max(0, (nominalEndBeforeTax - nominalTax) / (1 + infl));
    },

    /**
     * One-time contribution total for a member and plan year (1-based).
     * Buckets: concessional, nonConcessional, spouse (received), downsizer.
     * Legacy `{ year, amount }` on the bucket itself counts as non-concessional.
     */
    oneTimeContributionForYear(oneTimeContributions, personKey, type, targetYear) {
        const bucket = oneTimeContributions?.[personKey];
        if (!bucket) return 0;
        let total = 0;
        if (type === 'nonConcessional' && bucket.year === targetYear) total += Number(bucket.amount) || 0;
        (bucket[type] || []).forEach((c) => {
            if (c.year === targetYear) total += Number(c.amount) || 0;
        });
        return total;
    },

    /**
     * One member-year of accumulation — shared by the deterministic, historical-trial and
     * Adviser Monte Carlo paths. Order: nominal-taxed return, fees, then end-of-year
     * contributions (capped, contributions tax), LISTO, co-contribution and downsizer.
     */
    accumulationMemberYear({
        balance,
        wage,
        age,
        working,
        capState,
        nominalReturn,
        inflation,
        inputs = {},
        member = 'user',
        insuranceFees = 0,
        additionalCc = 0,
        oneTimeCc = 0,
        personalNcc = 0,
        spouseNccReceived = 0,
        downsizer = 0,
    }) {
        const num = (v, d) => (v === '' || v === null || v === undefined || !Number.isFinite(Number(v)) ? d : Number(v));
        let bal = SimulationEngine.applyAccumulationReturnWithTax(balance, nominalReturn, inflation, inputs);
        if (bal > 0) {
            bal -= SimulationEngine.FeeManager.calculateMemberFees(
                bal,
                num(inputs.adminFees, 52),
                num(inputs.adminFeePercentage, 0.10),
                num(inputs.investmentFees, 0.5),
                insuranceFees
            );
        }
        const out = {
            balance: bal,
            allowedCc: 0,
            allowedNcc: 0,
            personalNccAllowed: 0,
            listo: 0,
            coContribution: 0,
            downsizer: 0,
            credited: 0,
        };
        const isPartner = member === 'partner';
        if (working) {
            const ccIntent = (wage > 0 ? calculateSGContributions(wage) : 0)
                + (Number(additionalCc) || 0)
                + (Number(oneTimeCc) || 0);
            const personalIntent = Math.max(0, Number(personalNcc) || 0);
            const nccIntent = personalIntent + Math.max(0, Number(spouseNccReceived) || 0);
            const workTestMet = (isPartner ? inputs.partnerEnableWorkTest : inputs.enableWorkTest) !== false;
            const allowed = SimulationEngine.SGManager.capAnnualContributions({
                concessional: ccIntent,
                nonConcessional: nccIntent,
                totalSuperBalance: bal,
                age,
                state: capState,
                workTestMet,
                enableWorkTest: workTestMet,
            });
            out.allowedCc = allowed.concessional;
            out.allowedNcc = allowed.nonConcessional;
            out.personalNccAllowed = Math.min(personalIntent, allowed.nonConcessional);
            const netCc = SimulationEngine.SGManager.netOfContributionsTax(allowed.concessional, wage);
            bal += netCc + allowed.nonConcessional;
            out.credited += netCc + allowed.nonConcessional;

            const listoEnabled = inputs.enableLISTO !== false && inputs.enableLISTO !== 'false';
            if (listoEnabled && allowed.concessional > 0) {
                const atiInput = isPartner ? inputs.partnerListoAdjustedTaxableIncome : inputs.listoAdjustedTaxableIncome;
                const ati = atiInput != null && atiInput !== '' ? Number(atiInput) : (wage || 0);
                const listo = SimulationEngine.SGManager.calculateLISTO(ati, allowed.concessional);
                if (listo.eligible && listo.amount > 0) {
                    out.listo = listo.amount;
                    bal += listo.amount;
                    out.credited += listo.amount;
                }
            }
            if (inputs.enableCoContribution && out.personalNccAllowed > 0) {
                const co = SimulationEngine.SGManager.calculateCoContribution(wage, out.personalNccAllowed, age);
                if (co.eligible && co.amount > 0) {
                    out.coContribution = co.amount;
                    bal += co.amount;
                    out.credited += co.amount;
                }
            }
        }
        const downsizerCfg = FinancialConfig.CONTRIBUTION_CAPS.DOWNSIZER || {};
        const downsizerAmount = Math.max(0, Number(downsizer) || 0);
        if (downsizerAmount > 0 && age >= (Number(downsizerCfg.MIN_AGE) || 55)) {
            out.downsizer = Math.min(downsizerAmount, Number(downsizerCfg.MAX_CONTRIBUTION_PER_PERSON) || 300000);
            bal += out.downsizer;
            out.credited += out.downsizer;
        }
        out.balance = bal;
        return out;
    },

    /** Transition-to-retirement drawdown on the pension-phase share of a balance. */
    ttrDrawdown(balance, age, accumulationPhasePercent = 100, drawdownRatePct = 4) {
        const pensionShare = Math.max(0, 100 - (Number(accumulationPhasePercent) || 0)) / 100;
        const rate = Math.max(0.02, SimulationEngine.getMinimumWithdrawalRate(age), Math.min(0.10, (Number(drawdownRatePct) || 0) / 100));
        return Math.max(0, balance) * pensionShare * rate;
    },

    // Internal helper: Division 296 additional tax — SSOT via FinancialConfig.
    _division296Tax(earningsForThisMember, tsbEnd, taxOptions = {}) {
        return calculateDivision296MemberAccumulationSurcharge(
            earningsForThisMember,
            tsbEnd,
            taxOptions
        );
    },

    // Get minimum withdrawal rate based on age
    getMinimumWithdrawalRate: getMinimumWithdrawalRate,

    // Calculate tax on withdrawals
    calculateTax: (withdrawal, age, isSuper) => {
        if (!isSuper || age >= 60) return 0;
        return withdrawal * FinancialConfig.SUPER_TAX_RATES.WITHDRAWAL_TAX_UNDER_60;
    },

    // Calculate earnings tax for separated accounts
    calculateEarningsTax(accounts, portfolioReturn, portfolio, isCouple) {
        let totalEarningsTax = 0;
        
        // Calculate tax on accumulation account earnings (retirement phase is tax-free)
        const husbandAccumEarnings = accounts.husband.accum * portfolioReturn;
        const wifeAccumEarnings = isCouple ? accounts.wife.accum * portfolioReturn : 0;
        
        if (husbandAccumEarnings > 0) {
            const husbandTsbEnd = SimulationEngine.memberTsbAtYearEnd(accounts, 'husband', 0, husbandAccumEarnings);
            totalEarningsTax += SimulationEngine.calculateAccumulationTax(husbandAccumEarnings, portfolio, husbandTsbEnd);
        }
        
        if (wifeAccumEarnings > 0) {
            const wifeTsbEnd = SimulationEngine.memberTsbAtYearEnd(accounts, 'wife', 0, wifeAccumEarnings);
            totalEarningsTax += SimulationEngine.calculateAccumulationTax(wifeAccumEarnings, portfolio, wifeTsbEnd);
        }
        
        return totalEarningsTax;
    },

    // Calculate Age Pension using centralized configuration with comprehensive implementation
    // employmentIncome parameter added for Work Bonus calculation (2025 feature)
    calculatePension: (totalAssets, totalIncome, isCouple, isHomeowner, year = 0, customGrowthRate = null, rentPaidAnnual = 0, employmentIncome = 0) => {
        const growthRate = customGrowthRate !== null ? (customGrowthRate / 100) : FinancialConfig.AGE_PENSION.GROWTH_RATE;
        const pensionGrowthMultiplier = Math.pow(1 + growthRate, year);
        // Free areas / deeming thresholds stay CPI-flat in real terms; rate and cut-offs can grow.
        const params = getAgePensionParamsWithPensionGrowth(isCouple, isHomeowner, pensionGrowthMultiplier);
        
        const { maxPension, assetFreeThreshold, assetCutOff, incomeFreeThreshold, incomeCutOff,
                lowDeemThresh, lowDeemRate, highDeemRate, pensionSupplement, energySupplement } = params;

        // 1. DEEMING RULES - Calculate deemed income on financial assets
        // All financial assets are subject to deeming, including superannuation
        const deemedIncome = SimulationEngine.calculateDeemedIncome(totalAssets, lowDeemThresh, lowDeemRate, highDeemRate);
        
        // 2. WORK BONUS - Apply employment income exemption
        // Work Bonus allows Age Pension recipients to earn employment income with first $7,800/year exempt from income test
        // https://www.servicesaustralia.gov.au/work-bonus
        const workBonusConfig = FinancialConfig.AGE_PENSION.WORK_BONUS;
        let assessableEmploymentIncome = employmentIncome;
        if (workBonusConfig.ENABLED && employmentIncome > 0) {
            const workBonusExemption = Math.min(employmentIncome, workBonusConfig.ANNUAL_EXEMPTION);
            assessableEmploymentIncome = Math.max(0, employmentIncome - workBonusExemption);
        }
        
        // 3. INCOME TEST - Combined actual income + deemed income + assessable employment income
        // Note: totalIncome should NOT include employment income (which is passed separately for Work Bonus)
        const incomeForTest = totalIncome + deemedIncome + assessableEmploymentIncome;
        
        // 4. ASSET TEST - Calculate pension based on asset thresholds
        const assetPension = SimulationEngine.calculateAssetTestPension(totalAssets, assetFreeThreshold, assetCutOff, maxPension);
        
        // 5. INCOME TEST - Calculate pension based on income thresholds  
        const incomePension = SimulationEngine.calculateIncomeTestPension(incomeForTest, incomeFreeThreshold, incomeCutOff, maxPension);
        
        // 6. FINAL CALCULATION - Lower of the two tests, plus supplements
        const basePension = Math.min(assetPension, incomePension);
        
        // 7. SUPPLEMENTS - Add pension and energy supplements if eligible
        const pensionWithSupplements = SimulationEngine.addPensionSupplements(basePension, pensionSupplement, energySupplement);
        
        // 8. RENT ASSISTANCE - Add Commonwealth Rent Assistance if eligible
        // Only paid if receiving some base pension (not means-tested separately)
        const isPensioner = basePension > 0;
        const rentAssistance = SimulationEngine.calculateRentAssistance(rentPaidAnnual, isCouple, isPensioner, year, customGrowthRate);
        const finalPension = pensionWithSupplements + rentAssistance;

        return Math.max(0, finalPension);
    },

    /** Couple pension with staggered eligibility (matches Advanced household logic). */
    calculateHouseholdPension({
        totalAssets,
        otherIncome,
        isCouple,
        isHomeowner,
        year = 0,
        pensionGrowthRate = 0,
        ageA,
        ageB,
        includePension = true,
        rentPaidAnnual = 0,
        employmentIncome = 0,
    }) {
        const pensionAge = FinancialConfig.AGE_THRESHOLDS.AGE_PENSION_AGE;
        if (!includePension) return 0;

        if (!isCouple) {
            return ageA >= pensionAge
                ? (Number(SimulationEngine.calculatePension(
                    totalAssets, otherIncome, false, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome
                )) || 0)
                : 0;
        }

        const eligibleCount = (ageA >= pensionAge ? 1 : 0) + (ageB >= pensionAge ? 1 : 0);
        if (eligibleCount === 0) return 0;

        const coupleEntitlement = Number(
            SimulationEngine.calculatePension(
                totalAssets, otherIncome, true, isHomeowner, year, pensionGrowthRate, rentPaidAnnual, employmentIncome
            )
        ) || 0;

        return coupleEntitlement * (eligibleCount / 2);
    },

    // Calculate deemed income on financial assets (comprehensive deeming rules)
    calculateDeemedIncome(totalAssets, lowDeemThresh, lowDeemRate, highDeemRate) {
        if (totalAssets <= 0) return 0;
        
        // Tier 1: Low deeming rate on assets up to threshold
        const lowDeemAssets = Math.min(totalAssets, lowDeemThresh);
        const lowDeemIncome = lowDeemAssets * lowDeemRate;
        
        // Tier 2: High deeming rate on assets above threshold
        const highDeemAssets = Math.max(0, totalAssets - lowDeemThresh);
        const highDeemIncome = highDeemAssets * highDeemRate;
        
        return lowDeemIncome + highDeemIncome;
    },

    // Calculate pension based on asset test with proper phase-down rates
    calculateAssetTestPension(totalAssets, assetFreeThreshold, assetCutOff, maxPension) {
        // No pension if assets exceed cut-off
        if (totalAssets >= assetCutOff) return 0;
        
        // Full pension if assets below free threshold
        if (totalAssets <= assetFreeThreshold) return maxPension;
        
        // Phase-down: $3 reduction per $1000 of assets above threshold (fortnightly rate)
        // Annual rate = $3 * 26 fortnights = $78 per $1000
        const excessAssets = totalAssets - assetFreeThreshold;
        const annualReduction = (excessAssets / 1000) * 78;
        
        return Math.max(0, maxPension - annualReduction);
    },

    // Calculate pension based on income test with proper phase-down rates
    calculateIncomeTestPension(incomeForTest, incomeFreeThreshold, incomeCutOff, maxPension) {
        // No pension if income exceeds cut-off
        if (incomeForTest >= incomeCutOff) return 0;
        
        // Full pension if income below free threshold
        if (incomeForTest <= incomeFreeThreshold) return maxPension;
        
        // Phase-down: $0.50 reduction per $1 of income above threshold
        const excessIncome = incomeForTest - incomeFreeThreshold;
        const annualReduction = excessIncome * 0.5;
        
        return Math.max(0, maxPension - annualReduction);
    },

    // Add pension supplement if eligible for base pension
    addPensionSupplements(basePension, pensionSupplement, energySupplement) {
        // Supplements only paid if eligible for some base pension
        if (basePension <= 0) return basePension;
        
        // Add both pension supplement AND energy supplement (both are part of Age Pension payment)
        return basePension + pensionSupplement + energySupplement;
    },

    // Calculate Commonwealth Rent Assistance (CRA)
    // https://www.servicesaustralia.gov.au/rent-assistance
    calculateRentAssistance(rentPaidAnnual, isCouple, isPensioner = true, year = 0, customGrowthRate = null) {
        // Rent Assistance is only available to pensioners
        if (!isPensioner || rentPaidAnnual <= 0) return 0;
        
        // Apply growth rate for future years
        const growthRate = customGrowthRate !== null ? (customGrowthRate / 100) : FinancialConfig.AGE_PENSION.GROWTH_RATE;
        const growthMultiplier = Math.pow(1 + growthRate, year);
        
        // Get CRA parameters based on relationship status
        const craParams = isCouple ? 
            FinancialConfig.AGE_PENSION.RENT_ASSISTANCE.COUPLE : 
            FinancialConfig.AGE_PENSION.RENT_ASSISTANCE.SINGLE;
        
        // Apply growth multiplier to thresholds and maximums
        const minRent = craParams.MIN_RENT * growthMultiplier;
        const maxPayment = craParams.MAX_PAYMENT * growthMultiplier;
        const taperRate = craParams.TAPER_RATE;
        
        // Calculate CRA
        // CRA = min(max_payment, taper_rate * (rent_paid - min_rent))
        const rentAboveThreshold = Math.max(0, rentPaidAnnual - minRent);
        const calculatedCRA = rentAboveThreshold * taperRate;
        
        return Math.min(maxPayment, Math.max(0, calculatedCRA));
    },

    // Calculates the return for a given portfolio allocation
    getPortfolioReturn(yearData, portfolio) {
        const { sp500, ausShares, intlShares, bonds, cash, frankingCredits, infrastructurePct, infrastructureReturnRate } = portfolio;
        
        // Calculate returns for traditional assets
        const grossReturn = (sp500 / 100) * yearData.sp500 + (ausShares / 100) * yearData.ausShares + (intlShares / 100) * yearData.intlShares + (bonds / 100) * yearData.bonds + (cash / 100) * yearData.cash;
        const frankingBoost = (ausShares / 100) * FinancialConfig.PORTFOLIO.DEFAULT_FRANKING_CREDIT_BOOST * (frankingCredits / 100);
        
        // Add infrastructure returns if present (infrastructure has a fixed return rate, not market-dependent)
        const infrastructureReturn = (infrastructurePct && infrastructureReturnRate) 
            ? (infrastructurePct / 100) * (infrastructureReturnRate / 100)
            : 0;
        
        return grossReturn + frankingBoost + infrastructureReturn;
    },

    // Helper function to calculate portfolio return for assumed returns
    getAssumedPortfolioReturn(assumedReturn, assumedInflation, portfolio) {
        // Fisher real return — same basis as buildConstantAssumedReturns and the MC paths
        return ((1 + assumedReturn / 100) / (1 + assumedInflation / 100)) - 1;
    },

    // Get pension parameters using centralized configuration
    getPensionParams: getAgePensionParams,

    // Get investment allocation for different risk profiles
    getInvestmentAllocation(option) {
        switch (option) {
            case 'Conservative':
                return { sp500: 15, ausShares: 10, intlShares: 5, bonds: 50, cash: 20 };
            case 'Balanced':
                return { sp500: 35, ausShares: 15, intlShares: 20, bonds: 20, cash: 10 };
            case 'High Growth':
                return { sp500: 45, ausShares: 20, intlShares: 25, bonds: 5, cash: 5 };
            default:
                return { sp500: 35, ausShares: 15, intlShares: 20, bonds: 20, cash: 10 }; // Default to Balanced
        }
    },

    // OLD DUPLICATE FUNCTION - RENAMED TO PREVENT CONFLICT
    // REMOVED: runAccumulationSimulationOLD - Consolidated into single runAccumulationSimulation function

    // SECOND DUPLICATE FUNCTION - RENAMED TO PREVENT CONFLICT
    // REMOVED: runAccumulationSimulationSECOND - Consolidated into single runAccumulationSimulation function

    // Get investment allocation for different risk profiles
    getInvestmentAllocation(option) {
        switch (option) {
            case 'Conservative':
                return {
                    australianShares: 20,
                    internationalShares: 10,
                    bonds: 50,
                    cash: 20
                };
            case 'Balanced':
                return {
                    australianShares: 35,
                    internationalShares: 25,
                    bonds: 25,
                    cash: 15
                };
            case 'High Growth':
                return {
                    australianShares: 45,
                    internationalShares: 35,
                    bonds: 15,
                    cash: 5
                };
            default:
                return {
                    australianShares: 35,
                    internationalShares: 25,
                    bonds: 25,
                    cash: 15
                };
        }
    },

    // Helper function to calculate total contributions (for Monte Carlo where we don't track year-by-year)
    calculateTotalContributionsForInputs(inputs, oneTimeContributions = {}) {
        const {
            currentAge = 0,
            retirementAge = 67,
            currentWage = 0,
            wageGrowthRate = 0.3, // Historical Australian real wage growth average
            additionalContributions = 0,
            nonConcessionalContributions = 0,
            spouseContribution = 0,
            partnerCurrentAge = 0,
            partnerRetirementAge = 67,
            partnerCurrentWage = 0,
            partnerAdditionalContributions = 0,
            partnerNonConcessionalContributions = 0,
            partnerSpouseContribution = 0,
            assumedInflation = 2.5
        } = inputs;

        const isCouple = partnerCurrentAge > 0;
        const yearsToRetirement = retirementAge - currentAge;
        const partnerYearsToRetirement = isCouple ? partnerRetirementAge - partnerCurrentAge : 0;
        
        let totalContributions = 0;
        let userWage = currentWage;
        let partnerWage = partnerCurrentWage;
        
        // Calculate annual contributions with wage growth
        const sgRate = FinancialConfig.SUPERANNUATION_GUARANTEE.RATE;
        const maxYears = Math.max(yearsToRetirement, partnerYearsToRetirement);
        for (let year = 0; year < maxYears; year++) {
            // User contributions
            if (year < yearsToRetirement) {
                if (userWage > 0) {
                    totalContributions += calculateSGContributions(userWage, sgRate);
                }
                totalContributions += additionalContributions + nonConcessionalContributions;
            }
            
            // Partner contributions
            if (isCouple && year < partnerYearsToRetirement) {
                if (partnerWage > 0) {
                    totalContributions += calculateSGContributions(partnerWage, sgRate);
                }
                totalContributions += partnerAdditionalContributions + partnerNonConcessionalContributions;
            }
            
            // Spouse contributions
            if (isCouple && spouseContribution && year < partnerYearsToRetirement) {
                totalContributions += spouseContribution; // You to partner
            }
            if (isCouple && partnerSpouseContribution && year < yearsToRetirement) {
                totalContributions += partnerSpouseContribution; // Partner to you
            }
            
            // Apply wage growth for next year
            userWage *= (1 + wageGrowthRate / 100);
            partnerWage *= (1 + wageGrowthRate / 100);
        }
        
        for (const personKey of ['user', 'partner']) {
            for (const type of ['concessional', 'nonConcessional', 'spouse', 'downsizer']) {
                (oneTimeContributions?.[personKey]?.[type] || []).forEach((contrib) => {
                    totalContributions += Number(contrib.amount) || 0;
                });
            }
        }
        
        return totalContributions;
    },

    // Monte Carlo simulation for accumulation calculator
    runAccumulationMonteCarlo(inputs, oneTimeContributions = {}, trials = 1000) {
        const results = [];
        
        for (let trial = 0; trial < trials; trial++) {
            const result = SimulationEngine.runSingleAccumulationTrial(inputs, oneTimeContributions);
            results.push(result.finalBalance);
        }
        
        // Sort results to calculate percentiles
        results.sort((a, b) => a - b);
        
        const p10 = results[Math.floor(results.length * 0.1)];
        const p50 = results[Math.floor(results.length * 0.5)];
        const p90 = results[Math.floor(results.length * 0.9)];
        const p5 = results[Math.floor(results.length * 0.05)];
        const p95 = results[Math.floor(results.length * 0.95)];
        
        // Calculate total contributions (same across all trials - doesn't depend on returns)
        const totalContributions = SimulationEngine.calculateTotalContributionsForInputs(inputs, oneTimeContributions);
        
        // Calculate non-productive asset values at retirement (with capital growth)
        const currentAge = inputs.currentAge || 0;
        const retirementAge = inputs.retirementAge || 67;
        const yearsElapsed = Math.max(0, retirementAge - currentAge);
        const residentialPropertyValue = inputs.residentialPropertyValue || 0;
        const residentialCapitalGrowth = (inputs.residentialCapitalGrowth || 4) / 100;
        const commercialPropertyValue = inputs.commercialPropertyValue || 0;
        const commercialCapitalGrowth = (inputs.commercialCapitalGrowth || 4) / 100;
        const collectiblesValue = inputs.collectiblesValue || 0;
        const collectiblesCapitalGrowth = (inputs.collectiblesCapitalGrowth || 0) / 100;
        const otherAlternativesValue = inputs.otherAlternativesValue || 0;
        const otherAlternativesCapitalGrowth = (inputs.otherAlternativesCapitalGrowth || 0) / 100;
        
        const finalResidentialValue = residentialPropertyValue * Math.pow(1 + residentialCapitalGrowth, yearsElapsed);
        const finalCommercialValue = commercialPropertyValue * Math.pow(1 + commercialCapitalGrowth, yearsElapsed);
        const finalCollectiblesValue = collectiblesValue * Math.pow(1 + collectiblesCapitalGrowth, yearsElapsed);
        const finalOtherAlternativesValue = otherAlternativesValue * Math.pow(1 + otherAlternativesCapitalGrowth, yearsElapsed);
        
        const totalNonProductiveAssets = finalResidentialValue + finalCommercialValue + finalCollectiblesValue + finalOtherAlternativesValue;
        
        console.log('🏠 Monte Carlo Non-Productive Assets:', {
            yearsElapsed,
            finalCommercialValue,
            finalCollectiblesValue,
            finalOtherAlternativesValue,
            totalNonProductiveAssets
        });
        
        return {
            finalBalances: {
                median: Math.round(p50),
                p10: Math.round(p10),
                p90: Math.round(p90),
                bestCase: Math.round(p95),
                worstCase: Math.round(p5),
                total: Math.round(p50),
                // Non-productive assets
                nonProductiveAssets: {
                    residentialProperty: Math.round(finalResidentialValue),
                    commercialProperty: Math.round(finalCommercialValue),
                    collectibles: Math.round(finalCollectiblesValue),
                    otherAlternatives: Math.round(finalOtherAlternativesValue),
                    total: Math.round(totalNonProductiveAssets)
                },
                combinedWealth: Math.round(p50 + totalNonProductiveAssets)
            },
            totalContributions: Math.round(totalContributions),
            yearlyData: null, // No single projection for Monte Carlo
            lifeExpectancy: {
                p50: 85,
                p25: 90,
                p10: 95,
                p5: 98
            },
            trials: trials
        };
    },

    // Single Monte Carlo trial — same year logic as assumed accumulation path (#11)
    runSingleAccumulationTrial(inputs, oneTimeContributions = {}) {
        const historicalReturns = Object.values(historicalData);
        const portfolio = {
            sp500: Number(inputs.sp500 ?? 35),
            ausShares: Number(inputs.ausShares ?? inputs.australianShares ?? 15),
            intlShares: Number(inputs.intlShares ?? inputs.internationalShares ?? 20),
            bonds: Number(inputs.bonds ?? 20),
            cash: Number(inputs.cash ?? 10),
            frankingCredits: Number(inputs.frankingCredits ?? 80),
        };

        const userYearsToRetirement = (inputs.retirementAge || 67) - (inputs.currentAge || 55);
        const partnerYearsToRetirement = inputs.partnerCurrentAge
            ? (inputs.partnerRetirementAge || 67) - inputs.partnerCurrentAge
            : 0;
        const simulationYears = Math.max(userYearsToRetirement, partnerYearsToRetirement);

        let userBalance = Number(inputs.currentSuper) || 0;
        let partnerBalance = Number(inputs.partnerCurrentSuper) || 0;
        let userWage = Number(inputs.currentWage) || 0;
        let partnerWage = Number(inputs.partnerCurrentWage) || 0;
        const userCapState = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
        const partnerCapState = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
        const isCouple = Boolean(inputs.partnerCurrentAge);
        const wageGrowthRate = Number(inputs.wageGrowthRate ?? 0.3);
        const insuranceFeesAnnual = Number(inputs.insuranceFees) || 0;
        for (let year = 0; year < simulationYears; year++) {
            const randomYearData = historicalReturns[Math.floor(Math.random() * historicalReturns.length)];
            const nominalR = Number(SimulationEngine.getPortfolioReturn(randomYearData, portfolio)) || 0;
            const infl = randomYearData?.inflation || 0;
            const currentSimAge = (inputs.currentAge || 0) + year;
            const partnerSimAge = isCouple ? (inputs.partnerCurrentAge || 0) + year : 0;
            const memberYear = (personKey, opts) => SimulationEngine.accumulationMemberYear({
                nominalReturn: nominalR,
                inflation: infl,
                inputs,
                member: personKey,
                insuranceFees: opts.balance > 0 ? (isCouple ? insuranceFeesAnnual / 2 : insuranceFeesAnnual) : 0,
                oneTimeCc: SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'concessional', year + 1),
                personalNcc: opts.recurringNcc
                    + SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'nonConcessional', year + 1),
                spouseNccReceived: opts.spouseNcc
                    + SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'spouse', year + 1),
                downsizer: SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'downsizer', year + 1),
                ...opts,
            });

            userBalance = memberYear('user', {
                balance: userBalance,
                wage: userWage,
                age: currentSimAge,
                capState: userCapState,
                additionalCc: Number(inputs.additionalContributions) || 0,
                recurringNcc: Number(inputs.nonConcessionalContributions) || 0,
                spouseNcc: isCouple ? Number(inputs.partnerSpouseContribution) || 0 : 0,
                working: year < userYearsToRetirement,
            }).balance;
            if (isCouple) {
                partnerBalance = memberYear('partner', {
                    balance: partnerBalance,
                    wage: partnerWage,
                    age: partnerSimAge,
                    capState: partnerCapState,
                    additionalCc: Number(inputs.partnerAdditionalContributions) || 0,
                    recurringNcc: Number(inputs.partnerNonConcessionalContributions) || 0,
                    spouseNcc: Number(inputs.spouseContribution) || 0,
                    working: year < partnerYearsToRetirement,
                }).balance;
            }

            if (inputs.enableTTR && currentSimAge >= (inputs.ttrStartAge || 60)) {
                userBalance -= SimulationEngine.ttrDrawdown(userBalance, currentSimAge, inputs.accumulationPhasePercent ?? 100, inputs.ttrDrawdownRate || 4);
            }
            if (isCouple && inputs.partnerEnableTTR && partnerSimAge >= (inputs.partnerTtrStartAge || 60)) {
                partnerBalance -= SimulationEngine.ttrDrawdown(partnerBalance, partnerSimAge, inputs.partnerAccumulationPhasePercent ?? 100, inputs.partnerTtrDrawdownRate || 4);
            }
            userWage *= (1 + wageGrowthRate / 100);
            if (isCouple) partnerWage *= (1 + wageGrowthRate / 100);
        }

        return { finalBalance: userBalance + partnerBalance };
    },

    // Run accumulation simulation with TTR support
    runAccumulationSimulation(inputs, oneTimeContributions = {}, useRandomHistoricalData = false) {
        console.log('🚀 runAccumulationSimulation called with inputs:', {
            commercialPropertyValue: inputs.commercialPropertyValue,
            collectiblesValue: inputs.collectiblesValue,
            residentialPropertyValue: inputs.residentialPropertyValue,
            otherAlternativesValue: inputs.otherAlternativesValue
        });
        try {
            const {
                currentAge = 0,
                retirementAge = 67,
                currentSuper = 0,
                currentWage = 0,
                wageGrowthRate = 0.3, // Historical Australian real wage growth average
                additionalContributions = 0,
                nonConcessionalContributions = 0,
                spouseContribution = 0,
                enableTTR = false,
                ttrStartAge = 60,
                ttrDrawdownRate = 4,
                accumulationPhasePercent = 100,
                partnerCurrentAge = 0,
                partnerRetirementAge = 67,
                partnerCurrentSuper = 0,
                partnerCurrentWage = 0,
                partnerAdditionalContributions = 0,
                partnerNonConcessionalContributions = 0,
                partnerSpouseContribution = 0,
                partnerEnableTTR = false,
                partnerTtrStartAge = 60,
                partnerTtrDrawdownRate = 4,
                partnerAccumulationPhasePercent = 100,
                assumedReturn = 7.5,
                assumedInflation = 2.5,
                investmentFees = 0.5,
                adminFees = 52,
                adminFeePercentage = 0.10,
                sp500 = 35,
                ausShares = 15,
                intlShares = 20,
                bonds = 20,
                cash = 10,
                frankingCredits = 80
            } = inputs;

            const isCouple = partnerCurrentAge > 0;
            const yearsToRetirement = retirementAge - currentAge;
            const partnerYearsToRetirement = isCouple ? partnerRetirementAge - partnerCurrentAge : 0;
            const simulationYears = Math.max(yearsToRetirement, partnerYearsToRetirement);

            // Initialize balances
            let userBalance = currentSuper;
            let partnerBalance = isCouple ? partnerCurrentSuper : 0;
            let userWage = currentWage;
            let partnerWage = isCouple ? partnerCurrentWage : 0;
            const userCapState = SimulationEngine.SGManager.createAnnualCapState(inputs.carryForwardBalances);
            const partnerCapState = SimulationEngine.SGManager.createAnnualCapState(inputs.partnerCarryForwardBalances);
            // Initialize TTR withdrawal tracking
            let userTtrWithdrawal = 0;
            let partnerTtrWithdrawal = 0;

            // Portfolio allocation - check if we should use historical or assumed returns
            const portfolio = { sp500, ausShares, intlShares, bonds, cash, frankingCredits };
            // Check if we should run Monte Carlo simulation
            if (inputs.accumulationMode === 'historical') {
                return SimulationEngine.runAccumulationMonteCarlo(inputs, oneTimeContributions);
            }
            // For assumed returns, keep nominal return for tax, then report/store real dollars.
            const portfolioReturn = SimulationEngine.getAssumedPortfolioReturn(assumedReturn, assumedInflation, portfolio);
            // Simulation results
            const userData = [];
            const partnerData = [];
            const combinedData = [];
            
            // Track total contributions made over the simulation
            let totalContributions = 0;

            // CRITICAL FIX: Add Year 0 data (starting balances) so chart doesn't start at $0
            userData.push({
                year: 0,
                age: currentAge,
                balance: userBalance,
                partnerAge: isCouple ? partnerCurrentAge : null,
                coContribution: 0,
                listo: 0
            });

            if (isCouple) {
                partnerData.push({
                    year: 0,
                    age: partnerCurrentAge,
                    balance: partnerBalance,
                    partnerAge: currentAge,
                    coContribution: 0,
                    listo: 0
                });
            }

            combinedData.push({
                year: 0,
                userAge: currentAge,
                partnerAge: isCouple ? partnerCurrentAge : null,
                userBalance: userBalance,
                partnerBalance: isCouple ? partnerBalance : 0,
                totalBalance: userBalance + (isCouple ? partnerBalance : 0),
                userCoContribution: 0,
                partnerCoContribution: 0,
                totalCoContribution: 0,
                userListo: 0,
                partnerListo: 0,
                totalListo: 0
            });

            for (let year = 0; year < simulationYears; year++) {
                const currentSimAge = currentAge + year;
                const partnerSimAge = isCouple ? partnerCurrentAge + year : 0;
                
                const insuranceFeesAnnual = Number(inputs.insuranceFees) || 0;
                const memberYear = (personKey, opts) => SimulationEngine.accumulationMemberYear({
                    nominalReturn: assumedReturn / 100,
                    inflation: assumedInflation / 100,
                    inputs,
                    member: personKey,
                    insuranceFees: opts.balance > 0 ? (isCouple ? insuranceFeesAnnual / 2 : insuranceFeesAnnual) : 0,
                    oneTimeCc: SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'concessional', year + 1),
                    personalNcc: opts.recurringNcc
                        + SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'nonConcessional', year + 1),
                    spouseNccReceived: opts.spouseNcc
                        + SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'spouse', year + 1),
                    downsizer: SimulationEngine.oneTimeContributionForYear(oneTimeContributions, personKey, 'downsizer', year + 1),
                    ...opts,
                });

                const userApplied = memberYear('user', {
                    balance: userBalance,
                    wage: userWage,
                    age: currentSimAge,
                    capState: userCapState,
                    additionalCc: additionalContributions,
                    recurringNcc: Number(nonConcessionalContributions) || 0,
                    spouseNcc: isCouple ? Number(partnerSpouseContribution) || 0 : 0,
                    working: year < yearsToRetirement,
                });
                userBalance = userApplied.balance;
                totalContributions += userApplied.credited;
                const userCoContrib = userApplied.coContribution;
                const userListo = userApplied.listo;

                let partnerCoContrib = 0;
                let partnerListo = 0;
                if (isCouple) {
                    const partnerApplied = memberYear('partner', {
                        balance: partnerBalance,
                        wage: partnerWage,
                        age: partnerSimAge,
                        capState: partnerCapState,
                        additionalCc: partnerAdditionalContributions,
                        recurringNcc: Number(partnerNonConcessionalContributions) || 0,
                        spouseNcc: Number(spouseContribution) || 0,
                        working: year < partnerYearsToRetirement,
                    });
                    partnerBalance = partnerApplied.balance;
                    totalContributions += partnerApplied.credited;
                    partnerCoContrib = partnerApplied.coContribution;
                    partnerListo = partnerApplied.listo;
                }

                userTtrWithdrawal = enableTTR && currentSimAge >= ttrStartAge
                    ? SimulationEngine.ttrDrawdown(userBalance, currentSimAge, accumulationPhasePercent, ttrDrawdownRate)
                    : 0;
                userBalance -= userTtrWithdrawal;
                partnerTtrWithdrawal = isCouple && partnerEnableTTR && partnerSimAge >= partnerTtrStartAge
                    ? SimulationEngine.ttrDrawdown(partnerBalance, partnerSimAge, partnerAccumulationPhasePercent, partnerTtrDrawdownRate)
                    : 0;
                partnerBalance -= partnerTtrWithdrawal;

                // REMOVED DUPLICATE: Contributions already added above (lines 1148-1179)
                // This was adding contributions TWICE which inflated balances

                // Apply wage growth
                userWage *= (1 + wageGrowthRate / 100);
                if (isCouple) partnerWage *= (1 + wageGrowthRate / 100);

                // Store data
                userData.push({
                    year: year + 1,
                    age: currentSimAge + 1,
                    balance: userBalance,
                    partnerAge: isCouple ? partnerSimAge + 1 : null,
                    coContribution: userCoContrib,
                    listo: userListo
                });

                if (isCouple) {
                    partnerData.push({
                        year: year + 1,
                        age: partnerSimAge + 1,
                        balance: partnerBalance,
                        partnerAge: currentSimAge + 1,
                        coContribution: partnerCoContrib,
                        listo: partnerListo
                    });
                }

                combinedData.push({
                    year: year + 1,
                    userAge: currentSimAge + 1,
                    partnerAge: isCouple ? partnerSimAge + 1 : null,
                    userBalance: userBalance,
                    partnerBalance: isCouple ? partnerBalance : 0,
                    totalBalance: userBalance + (isCouple ? partnerBalance : 0),
                    userCoContribution: userCoContrib,
                    partnerCoContribution: partnerCoContrib,
                    totalCoContribution: userCoContrib + partnerCoContrib,
                    userListo,
                    partnerListo,
                    totalListo: userListo + partnerListo
                });
            }

            const totalFinalBalance = userBalance + (isCouple ? partnerBalance : 0);
            
            // Calculate non-productive asset values at retirement (with capital growth)
            const yearsElapsed = Math.max(0, retirementAge - currentAge);
            const residentialPropertyValue = inputs.residentialPropertyValue || 0;
            const residentialCapitalGrowth = (inputs.residentialCapitalGrowth || 4) / 100;
            const commercialPropertyValue = inputs.commercialPropertyValue || 0;
            const commercialCapitalGrowth = (inputs.commercialCapitalGrowth || 4) / 100;
            const collectiblesValue = inputs.collectiblesValue || 0;
            const collectiblesCapitalGrowth = (inputs.collectiblesCapitalGrowth || 0) / 100;
            const otherAlternativesValue = inputs.otherAlternativesValue || 0;
            const otherAlternativesCapitalGrowth = (inputs.otherAlternativesCapitalGrowth || 0) / 100;
            
            console.log('🏠 Non-Productive Assets Calculation:', {
                yearsElapsed,
                residentialPropertyValue,
                commercialPropertyValue,
                collectiblesValue,
                otherAlternativesValue,
                inputs: {
                    residentialPropertyValue: inputs.residentialPropertyValue,
                    commercialPropertyValue: inputs.commercialPropertyValue,
                    collectiblesValue: inputs.collectiblesValue,
                    otherAlternativesValue: inputs.otherAlternativesValue
                }
            });
            
            const finalResidentialValue = residentialPropertyValue * Math.pow(1 + residentialCapitalGrowth, yearsElapsed);
            const finalCommercialValue = commercialPropertyValue * Math.pow(1 + commercialCapitalGrowth, yearsElapsed);
            const finalCollectiblesValue = collectiblesValue * Math.pow(1 + collectiblesCapitalGrowth, yearsElapsed);
            const finalOtherAlternativesValue = otherAlternativesValue * Math.pow(1 + otherAlternativesCapitalGrowth, yearsElapsed);
            
            const totalNonProductiveAssets = finalResidentialValue + finalCommercialValue + finalCollectiblesValue + finalOtherAlternativesValue;
            const totalCombinedWealth = totalFinalBalance + totalNonProductiveAssets;
            
            console.log('🏠 Final Non-Productive Assets:', {
                finalResidentialValue,
                finalCommercialValue,
                finalCollectiblesValue,
                finalOtherAlternativesValue,
                totalNonProductiveAssets,
                totalCombinedWealth
            });
            
            const result = {
                userData,
                partnerData,
                combinedData,
                yearlyData: combinedData, // This is what the graph expects
                finalBalances: {
                    median: totalFinalBalance,
                    bestCase: totalFinalBalance,
                    worstCase: totalFinalBalance,
                    user: userBalance,
                    partner: isCouple ? partnerBalance : 0,
                    total: totalFinalBalance,
                    // Non-productive assets
                    nonProductiveAssets: {
                        residentialProperty: Math.round(finalResidentialValue),
                        commercialProperty: Math.round(finalCommercialValue),
                        collectibles: Math.round(finalCollectiblesValue),
                        otherAlternatives: Math.round(finalOtherAlternativesValue),
                        total: Math.round(totalNonProductiveAssets)
                    },
                    combinedWealth: Math.round(totalCombinedWealth)
                },
                totalContributions: Math.round(totalContributions),
                ttrWithdrawals: {
                    user: userTtrWithdrawal,
                    partner: partnerTtrWithdrawal
                }
            };
            
            // Debug logging
            return result;

        } catch (error) {
            // Accumulation simulation error
            return { error: 'Calculation failed: ' + error.message };
        }
    },

    // Sequence of Returns Risk calculation functions (single source of truth)
    calculateMaxSustainableIncomeWithSequenceRisk(initialBalance, years, normalReturn, sequenceRiskReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, pensionGrowthRate = 0) {
        // Use binary search to find maximum sustainable income with sequence risk
        let low = 0;
        let high = initialBalance * 0.5; // Start with 50% of balance as upper bound (more realistic)
        let tolerance = 0.01; // $0.01 tolerance for maximum precision
        
        // Helper function to check if a given income is sustainable with sequence risk
        const isIncomeSustainable = (testIncome) => {
            let balance = initialBalance;
            
            for (let year = 0; year < years; year++) {
                const totalAssets = balance + assessableAssets;
                const calculatedPension = includePension ? SimulationEngine.calculatePension(totalAssets, otherIncome, isCouple, isHomeowner, year, pensionGrowthRate) : 0;
                const totalOtherIncome = calculatedPension + otherIncome;
                const withdrawalFromSuper = balance > 0 ? Math.max(0, testIncome - totalOtherIncome) : 0;
                
                // Check if we can afford the super withdrawal
                if (withdrawalFromSuper > balance) {
                    return false;
                }
                
                // Apply sequence risk: poor returns for first 3 years, then normal returns
                const effectiveReturn = year < 3 ? sequenceRiskReturn : normalReturn;
                
                // Apply return
                balance = balance * (1 + effectiveReturn);
                
                // Subtract withdrawal
                balance = Math.max(0, balance - withdrawalFromSuper);
            }
            
            return balance >= 0;
        };
        
        // Use a much more conservative upper bound - maximum 6% of balance per year
        high = Math.min(high, initialBalance * 0.06);
        
        while (high - low > tolerance) {
            const testIncome = (low + high) / 2;
            
            if (isIncomeSustainable(testIncome)) {
                low = testIncome;
            } else {
                high = testIncome;
            }
        }
        
        return low;
    },

    calculateMaxSustainableIncomeWithSequenceRiskAndFees(initialBalance, years, normalReturn, sequenceRiskReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, adminFees, investmentFees, insuranceFees, adminFeePercentage = 0.10, pensionGrowthRate = null) {
        // Sequence risk function (currently has logic errors)
        // COMPREHENSIVE FIX: Use same upper bound logic as main MSI function
        let low = 0;
        let high = initialBalance * 0.20; // Start with 20% of balance per year
        
        // Add other income sources
        high += otherIncome;
        
        // Add pension capacity (but be realistic about it reducing with higher income)
        if (includePension) {
            const pensionParams = getAgePensionParams(isCouple, isHomeowner, 1, 1);
            high += pensionParams.maxPension * 0.5; // Use 50% of max pension as realistic upper bound
        }
        
        let tolerance = 100;
        let balanceAfter3YearsForDisplay = 0;
        let iterations = 0;
        const maxIterations = 100;
        
        // Helper function to check if a given income is sustainable with sequence risk and fees
        const isIncomeSustainable = (testIncome) => {
            let balance = initialBalance;
            
            for (let year = 0; year < years; year++) {
                const totalAssets = balance + assessableAssets;
                const currentAge = 65 + year; // Assume starting retirement at 65
                const pensionAge = 67; // Age pension eligibility age
                const calculatedPension = (includePension && currentAge >= pensionAge) ? SimulationEngine.calculatePension(totalAssets, otherIncome, isCouple, isHomeowner, year, pensionGrowthRate) : 0;
                const totalOtherIncome = calculatedPension + otherIncome;
                const withdrawalFromSuper = balance > 0 ? Math.max(0, testIncome - totalOtherIncome) : 0;
                
                // If the required income is less than or equal to other income sources, it's sustainable
                if (testIncome <= totalOtherIncome) {
                    // No super withdrawal needed, income is fully covered by other sources
                    // Don't apply sequence risk to balance since we're not using it
                    // Just apply normal returns to maintain the balance
                    balance = balance * (1 + normalReturn);
                    
                    // Subtract fees
                    const baseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(balance, adminFees, 0.10);
                    const additionalInvestmentFees = balance * investmentFees;
                    const totalFees = baseFees + additionalInvestmentFees;
                    balance = Math.max(0, balance - totalFees);
                    
                    // No withdrawal needed
                } else {
                    // Check if we can afford the super withdrawal
                    if (withdrawalFromSuper > balance) {
                        return false;
                    }
                    
                    // Apply sequence risk: poor returns for first 3 years, then normal returns
                    const effectiveReturn = year < 3 ? sequenceRiskReturn : normalReturn;
                    
                    // Apply return
                    balance = balance * (1 + effectiveReturn);
                    
                    // Subtract fees
                    const baseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(balance, adminFees, adminFeePercentage);
                    const additionalInvestmentFees = balance * investmentFees;
                    const totalFees = baseFees + additionalInvestmentFees;
                    balance = Math.max(0, balance - totalFees);
                    
                    // Subtract withdrawal
                    balance = Math.max(0, balance - withdrawalFromSuper);
                }
                
                // Store balance after 3 years for display
                if (year === 2) {
                    balanceAfter3YearsForDisplay = balance;
                }
            }
            
            return balance >= 0;
        };
        
        while (high - low > tolerance && iterations < maxIterations) {
            const testIncome = (low + high) / 2;
            const sustainable = isIncomeSustainable(testIncome);
            
            if (sustainable) {
                low = testIncome;
            } else {
                high = testIncome;
            }
            iterations++;
        }
        
        const result = { 
            maxSustainableIncome: Math.round(low), 
            balanceAfter3Years: Math.round(balanceAfter3YearsForDisplay) 
        };
        // Function result (disabled due to logic errors)
        return result;
    },

    // Monte Carlo simulation for accumulation calculator (single source of truth)
    runMonteCarloSimulation(inputs, trials = 1000) {
        const results = [];
        
        for (let i = 0; i < trials; i++) {
            // Run a single simulation with randomized historical data
            const result = this.runAccumulationSimulation(inputs, null, true); // true = use random historical data
            results.push(result.finalBalances.median);
        }
        
        // Sort results to find percentiles
        results.sort((a, b) => a - b);
        
        const worst = results[Math.floor(results.length * 0.1)]; // 10th percentile
        const median = results[Math.floor(results.length * 0.5)]; // 50th percentile  
        const best = results[Math.floor(results.length * 0.9)]; // 90th percentile
        return {
            worst: Math.round(worst),
            median: Math.round(median),
            best: Math.round(best),
            trials: trials
        };
    },

    // Basic maximum sustainable income calculation (single source of truth)
    calculateMaxSustainableIncome(initialBalance, years, netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, adminFees = 0, investmentFees = 0, insuranceFees = 0, targetFinalBalance = 0, adminFeePercentage = 0.10, pensionGrowthRate = 0, startingAge = 65) {
        return SimulationEngine.calculateMaxSustainableIncomeWithFees(initialBalance, years, netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, adminFees, investmentFees, insuranceFees, targetFinalBalance, adminFeePercentage, pensionGrowthRate, startingAge);
    },

    // NEW UNIFIED MSI WRAPPER (non-breaking): delegates based on inputs
    calculateMaxSustainableIncomeUnified(params) {
        const {
            // Core parameters
            initialBalance,
            years,
            
            // Return parameters (choose one approach)
            netReturn,           // For simple calculations
            returnsByYear,       // For historical data
            weights,             // For portfolio allocation
            strategy,            // Withdrawal strategy
            
            // Income parameters
            otherIncome = 0,
            assessableAssets = 0,
            
            // Demographics
            isCouple = false,
            isHomeowner = true,
            includePension = true,
            
            // Fees
            adminFees = 0,
            investmentFees = 0,
            insuranceFees = 0,
            adminFeePercentage = 0.10,
            
            // Advanced options
            targetFinalBalance = 0,
            pensionGrowthRate = 0,
            sequenceRiskReturn = null,
            tolerance = 100
        } = params;

        // Validate required parameters
        if (initialBalance === undefined || initialBalance === null || years === undefined || years === null) {
            throw new Error('initialBalance and years are required');
        }

        // Determine calculation approach based on available data
        if (returnsByYear && returnsByYear.length > 0) {
            return this._calculateWithHistoricalData(params);
        } else if (weights && strategy) {
            return this._calculateWithPortfolioStrategy(params);
        } else if (netReturn !== undefined) {
            return this._calculateWithSimpleReturn(params);
        } else {
            throw new Error('Must provide either netReturn, returnsByYear, or weights+strategy');
        }
    },

    // Helper functions for unified MSI calculator
    _calculateWithSimpleReturn(params) {
        const {
            initialBalance,
            years,
            netReturn,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate,
            startingAge // CRITICAL: Pass starting age through
        } = params;

        // Binary search for maximum sustainable income
        let low = 0;
        let high = initialBalance * 0.20; // Start with 20% of balance as upper bound
        let result = 0;

        while (high - low > 1) {
            const testIncome = (low + high) / 2;
            
            if (this._doesPortfolioSurviveSimple(testIncome, {
                initialBalance,
                years,
                netReturn,
                otherIncome,
                assessableAssets,
                isCouple,
                isHomeowner,
                includePension,
                adminFees,
                investmentFees,
                insuranceFees,
                adminFeePercentage,
                targetFinalBalance,
                pensionGrowthRate,
                startingAge // CRITICAL: Pass starting age through
            })) {
                result = testIncome;
                low = testIncome;
            } else {
                high = testIncome;
            }
        }

        return Math.round(result);
    },

    _calculateWithHistoricalData(params) {
        // Use historical returns for more accurate modeling
        const {
            initialBalance,
            years,
            returnsByYear,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate,
            startingAge // CRITICAL: Pass starting age through
        } = params;

        let low = 0;
        let high = initialBalance * 0.20;
        let result = 0;

        while (high - low > 1) {
            const testIncome = (low + high) / 2;
            
            if (this._doesPortfolioSurviveHistorical(testIncome, {
                initialBalance,
                years,
                returnsByYear,
                otherIncome,
                assessableAssets,
                isCouple,
                isHomeowner,
                includePension,
                adminFees,
                investmentFees,
                insuranceFees,
                adminFeePercentage,
                targetFinalBalance,
                pensionGrowthRate,
                startingAge // CRITICAL: Pass starting age through
            })) {
                result = testIncome;
                low = testIncome;
            } else {
                high = testIncome;
            }
        }

        return Math.round(result);
    },

    _calculateWithPortfolioStrategy(params) {
        // Use portfolio weights and withdrawal strategy
        const {
            initialBalance,
            years,
            weights,
            strategy,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate
        } = params;

        let low = 0;
        let high = initialBalance * 0.20;
        let result = 0;

        while (high - low > 1) {
            const testIncome = (low + high) / 2;
            
            if (this._doesPortfolioSurviveStrategy(testIncome, {
                initialBalance,
                years,
                weights,
                strategy,
                otherIncome,
                assessableAssets,
                isCouple,
                isHomeowner,
                includePension,
                adminFees,
                investmentFees,
                insuranceFees,
                adminFeePercentage,
                targetFinalBalance,
                pensionGrowthRate
            })) {
                result = testIncome;
                low = testIncome;
            } else {
                high = testIncome;
            }
        }

        return Math.round(result);
    },

    _doesPortfolioSurviveSimple(income, params) {
        const {
            initialBalance,
            years,
            netReturn,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate,
            startingAge // CRITICAL: Use actual starting age, not hardcoded 65
        } = params;

        let balance = initialBalance;
        const age = startingAge || 65; // Use provided starting age or default to 65

        for (let year = 1; year <= years; year++) {
            const currentAge = age + year - 1;
            
            // Calculate age pension if applicable
            let agePension = 0;
            if (includePension && currentAge >= 67) {
                agePension = SimulationEngine.calculatePension(assessableAssets + balance, otherIncome, isCouple, isHomeowner, year - 1, pensionGrowthRate);
            }

            // Calculate withdrawal from super (income minus other sources)
            const totalOtherIncome = agePension + otherIncome;
            const withdrawalFromSuper = Math.max(0, income - totalOtherIncome);
            
            // Apply return FIRST (match chart generation order)
            balance = balance * (1 + netReturn);
            
            // Calculate and subtract fees
            const baseFees = SimulationEngine.FeeManager.calculateAustralianSuperFees(balance, adminFees, adminFeePercentage);
            const additionalInvestmentFees = balance * investmentFees;
            const totalFees = baseFees + additionalInvestmentFees + insuranceFees;
            balance = Math.max(0, balance - totalFees);
            
            // Check if we can afford the withdrawal AFTER growth and fees
            if (withdrawalFromSuper > balance) {
                return false;
            }
            
            // Subtract withdrawal
            balance = Math.max(0, balance - withdrawalFromSuper);
        }

        // Check if target final balance is met
        return balance >= targetFinalBalance;
    },

    _doesPortfolioSurviveHistorical(income, params) {
        // Similar to _doesPortfolioSurviveSimple but uses historical returns
        const {
            initialBalance,
            years,
            returnsByYear,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate,
            startingAge // CRITICAL: Use actual starting age, not hardcoded 65
        } = params;

        let balance = initialBalance;
        const age = startingAge || 65; // Use provided starting age or default to 65

        for (let year = 1; year <= years; year++) {
            const currentAge = age + year - 1;
            const yearIndex = (year - 1) % returnsByYear.length; // Cycle through historical data
            const netReturn = returnsByYear[yearIndex];
            
            // Calculate age pension if applicable
            let agePension = 0;
            if (includePension) {
                const pensionParams = getAgePensionParams(currentAge, isCouple, isHomeowner);
                agePension = SimulationEngine.calculatePension(assessableAssets + balance, otherIncome, isCouple, isHomeowner, year - 1, pensionGrowthRate);
            }

            // Calculate fees
            const annualAdminFees = adminFees;
            const annualInvestmentFees = balance * investmentFees;
            const annualInsuranceFees = insuranceFees;
            const annualAdminFeePercentage = balance * adminFeePercentage;

            // Apply return and fees
            const grossReturn = balance * netReturn;
            const totalFees = annualAdminFees + annualInvestmentFees + annualInsuranceFees + annualAdminFeePercentage;
            
            balance = balance + grossReturn - income - totalFees;
            
            // Check if balance is sufficient
            if (balance < 0) {
                return false;
            }
        }

        return balance >= targetFinalBalance;
    },

    _doesPortfolioSurviveStrategy(income, params) {
        // Similar but uses portfolio weights and withdrawal strategy
        const {
            initialBalance,
            years,
            weights,
            strategy,
            otherIncome,
            assessableAssets,
            isCouple,
            isHomeowner,
            includePension,
            adminFees,
            investmentFees,
            insuranceFees,
            adminFeePercentage,
            targetFinalBalance,
            pensionGrowthRate
        } = params;

        let balance = initialBalance;
        const age = 65;

        for (let year = 1; year <= years; year++) {
            const currentAge = age + year - 1;
            
            // Calculate weighted portfolio return based on strategy
            const netReturn = this._calculatePortfolioReturn(weights, strategy, year);
            
            // Calculate age pension if applicable
            let agePension = 0;
            if (includePension) {
                const pensionParams = getAgePensionParams(currentAge, isCouple, isHomeowner);
                agePension = SimulationEngine.calculatePension(assessableAssets + balance, otherIncome, isCouple, isHomeowner, year - 1, pensionGrowthRate);
            }

            // Calculate fees
            const annualAdminFees = adminFees;
            const annualInvestmentFees = balance * investmentFees;
            const annualInsuranceFees = insuranceFees;
            const annualAdminFeePercentage = balance * adminFeePercentage;

            // Apply return and fees
            const grossReturn = balance * netReturn;
            const totalFees = annualAdminFees + annualInvestmentFees + annualInsuranceFees + annualAdminFeePercentage;
            
            balance = balance + grossReturn - income - totalFees;
            
            // Check if balance is sufficient
            if (balance < 0) {
                return false;
            }
        }

        return balance >= targetFinalBalance;
    },

    _calculatePortfolioReturn(weights, strategy, year) {
        // Calculate portfolio return based on weights and strategy
        // This is a simplified version - could be enhanced with historical data
        const baseReturn = 0.05; // 5% base return
        
        // Adjust based on strategy
        let strategyAdjustment = 0;
        switch (strategy) {
            case 'conservative':
                strategyAdjustment = -0.02; // -2%
                break;
            case 'balanced':
                strategyAdjustment = 0; // 0%
                break;
            case 'growth':
                strategyAdjustment = 0.02; // +2%
                break;
            default:
                strategyAdjustment = 0;
        }

        return baseReturn + strategyAdjustment;
    },

    // MONTHLY SIMULATION - More accurate than annual
    // Simulates one year using MONTHLY periods (12 months)
    _simulateOneYearMonthly(balance, annualIncome, year, params) {
        const { netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, 
                adminFees, investmentFees, insuranceFees, adminFeePercentage, pensionGrowthRate, startingAge,
                partnerStartingAge, memberShares = null } = params;
        
        const currentAge = startingAge + year;
        const partnerAge = (partnerStartingAge != null ? partnerStartingAge : startingAge) + year;
        
        // Calculate annual pension once (distributed monthly)
        const estimatedAssets = assessableAssets + Math.max(0, balance);
        const incomeForPension = Math.max(0, otherIncome);
        const pensionAnnual = includePension
            ? SimulationEngine.calculateHouseholdPension({
                totalAssets: estimatedAssets,
                otherIncome: incomeForPension,
                isCouple,
                isHomeowner,
                year,
                pensionGrowthRate,
                ageA: currentAge,
                ageB: partnerAge,
                includePension: true,
            })
            : 0;
        
        // Convert to monthly amounts
        const monthlyIncome = annualIncome / 12;
        const monthlyPension = pensionAnnual / 12;
        const monthlyOtherIncome = otherIncome / 12;
        const monthlyReturn = Math.pow(1 + netReturn, 1/12) - 1;
        
        // Fee parameters (annual values, applied monthly)
        const adminFeesNum = (adminFees !== undefined && adminFees !== null) ? Number(adminFees) : 52;
        const adminFeePercentageNum = (adminFeePercentage !== undefined && adminFeePercentage !== null) ? Number(adminFeePercentage) : 0.10;
        const investmentFeesNum = Number(investmentFees) || 0;
        const insuranceFeesNum = Number(insuranceFees) || 0;
        
        let currentBalance = balance;
        let totalWithdrawal = 0;
        let monthlyMinimum = 0; // Monthly portion of annual minimum withdrawal
        
        // Simulate 12 months
        for (let month = 0; month < 12; month++) {
            // Apply monthly growth FIRST
            currentBalance = currentBalance * (1 + monthlyReturn);
            
            // Calculate monthly need from super
            const totalMonthlyOtherIncome = monthlyPension + monthlyOtherIncome;
            const needFromSuper = Math.max(0, monthlyIncome - totalMonthlyOtherIncome);
            
            // CRITICAL: Enforce minimum withdrawal rates (monthly basis)
            // Check if we can access super and enforce minimums
            const currentAge = startingAge + year + (month / 12);
            const canAccessSuper = currentAge >= 60;
            
            let actualWithdrawal = 0;
            if (canAccessSuper) {
                // Get annual minimum rate and convert to monthly amount
                // The annual minimum applies to the balance at start of year, not each month
                // So we calculate it once for the year and divide by 12
                if (month === 0) {
                    // At start of year, calculate annual minimum required
                    const annualMinimumRate = this.getMinimumWithdrawalRate(currentAge);
                    // Store the monthly minimum for this year (divided by 12)
                    monthlyMinimum = (balance * annualMinimumRate) / 12;
                }
                
                // Enforce minimum withdrawals (Australian law requirement)
                actualWithdrawal = Math.max(Math.min(currentBalance, needFromSuper), monthlyMinimum);
            }
            
            currentBalance = currentBalance - actualWithdrawal;
            totalWithdrawal += actualWithdrawal;
            
            // Apply fees LAST (monthly); Basic passes investmentFees as a fraction, not percent points.
            const totalMonthlyFees = SimulationEngine.FeeManager.calculateHouseholdFees(currentBalance, {
                adminFees: adminFeesNum,
                adminFeePercentage: adminFeePercentageNum,
                investmentFees: investmentFeesNum * 100,
                insuranceFees: insuranceFeesNum,
            }, isCouple, memberShares) / 12;
            
            currentBalance = Math.max(0, currentBalance - totalMonthlyFees);
            
            // If balance goes to zero, stop
            if (currentBalance <= 0) {
                break;
            }
        }
        
        return {
            finalBalance: currentBalance,
            withdrawal: totalWithdrawal,
            pension: pensionAnnual
        };
    },

    // LEGACY: Keep annual simulation for backwards compatibility
    // This simulates a single year with given income
    _simulateOneYear(balance, income, year, params) {
        // Use monthly simulation for accuracy
        return this._simulateOneYearMonthly(balance, income, year, params);
    },

    // Maximum sustainable income with fees
    calculateMaxSustainableIncomeWithFees(initialBalance, years, netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, adminFees, investmentFees, insuranceFees, targetFinalBalance = 0, adminFeePercentage = 0.10, pensionGrowthRate = 0, startingAge = 65, partnerStartingAge = null, memberShares = null) {
        // DEBUG: Log for $1.3M balance
        const debugThis = Math.abs(initialBalance - 1305000) < 1000 && years === 35;
        if (debugThis) {
            console.log(`\n🔍 MSI CALCULATION DEBUG:`);
            console.log(`  initialBalance: $${initialBalance.toLocaleString()}`);
            console.log(`  years: ${years}`);
            console.log(`  netReturn: ${(netReturn*100).toFixed(2)}%`);
            console.log(`  adminFees: ${adminFees}`);
            console.log(`  adminFeePercentage: ${adminFeePercentage}`);
            console.log(`  investmentFees: ${investmentFees}`);
            console.log(`  Expected MSI (annuity formula): $${Math.round(initialBalance * netReturn / (1 - Math.pow(1 + netReturn, -years))).toLocaleString()}`);
        }
        
        const params = { netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, 
                        adminFees, investmentFees, insuranceFees, adminFeePercentage, pensionGrowthRate, startingAge,
                        partnerStartingAge: partnerStartingAge ?? startingAge, memberShares };
        
        // Calculate upper bound
        const estimatedPension = includePension ? SimulationEngine.calculateHouseholdPension({
            totalAssets: assessableAssets + initialBalance,
            otherIncome,
            isCouple,
            isHomeowner,
            year: 0,
            pensionGrowthRate,
            ageA: startingAge,
            ageB: partnerStartingAge ?? startingAge,
        }) : 0;
        const totalAvailableIncome = estimatedPension + otherIncome;
        
        let low = Math.max(0, totalAvailableIncome);
        // FIXED: Use a more reasonable upper bound that doesn't constrain low balances
        // For high returns (8.5%), even a modest balance can support good income
        let high = Math.max(initialBalance * 0.20, totalAvailableIncome * 3);
        
        let bestIncome = 0;
        let bestMinYearlyIncome = 0;
        let bestFinalBalance = initialBalance;
        const tolerance = 1; // Very tight tolerance to maximize income extraction
        
        if (debugThis) {
            console.log(`  Binary search range: $${low.toLocaleString()} to $${high.toLocaleString()}`);
        }
        
        // Binary search for maximum sustainable income
        for (let iter = 0; iter < 100; iter++) {
            const mid = (low + high) / 2;
            
            // Simulate full period
            let testBal = initialBalance;
            let sustainable = true;
            let minYearlyIncome = Infinity;
            
            for (let year = 0; year < years; year++) {
                const result = this._simulateOneYear(testBal, mid, year, params);
                
                // Track minimum income achieved to ensure target is met every year
                const yearIncome = result.withdrawal + result.pension + otherIncome;
                
                // DEBUG: Log when income doesn't match target
                if (debugThis && Math.abs(mid - 89700) < 100 && yearIncome < mid * 0.999) {
                    console.log(`    ⚠️ Year ${year + 1}: Withdrew only $${Math.round(result.withdrawal).toLocaleString()} (needed $${Math.round(mid).toLocaleString()}), balance: $${Math.round(testBal).toLocaleString()}`);
                }
                
                minYearlyIncome = Math.min(minYearlyIncome, yearIncome);
                
                testBal = result.finalBalance;
                if (testBal < 0) {
                    sustainable = false;
                    break;
                }
            }
            
            // Check if target income was achieved in every year ($1 tolerance for rounding only)
            const incomeAchieved = minYearlyIncome >= mid - 1;
            
            // Standard binary search: if sustainable, try higher; if not, go lower
            if (sustainable && incomeAchieved) {
                bestIncome = mid;
                bestMinYearlyIncome = minYearlyIncome;
                bestFinalBalance = testBal;
                if (debugThis && iter < 5) {
                    console.log(`  Iter ${iter}: $${Math.round(mid).toLocaleString()} → WORKS (final: $${Math.round(testBal).toLocaleString()})`);
                }
                low = mid; // This income works, try higher
            } else {
                if (debugThis && iter < 5) {
                    console.log(`  Iter ${iter}: $${Math.round(mid).toLocaleString()} → FAILS (sustainable=${sustainable}, achieved=${incomeAchieved}, minIncome=$${Math.round(minYearlyIncome).toLocaleString()})`);
                }
                high = mid; // Too aggressive, go lower
            }
            
            if (Math.abs(high - low) < tolerance) {
                if (debugThis) {
                    console.log(`  Converged after ${iter + 1} iterations`);
                    console.log(`  FINAL MSI: $${Math.round(bestIncome).toLocaleString()}\n`);
                }
                break;
            }
        }
        
        if (initialBalance === 1000000 && years === 30) {
            console.log(`calculateMaxSustainableIncomeWithFees returning bestIncome=$${Math.round(bestIncome).toLocaleString()}`);
        }
        
        return Math.round(Math.min(bestIncome, bestMinYearlyIncome || bestIncome));
    },

    // Calculate final balance with a specific income (monthly simulation for accuracy)
    calculateFinalBalanceWithIncome(initialBalance, years, income, netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, adminFees, investmentFees, insuranceFees, adminFeePercentage = 0.10, targetFinalBalance = 0, pensionGrowthRate = null, startingAge = 65, partnerStartingAge = null, memberShares = null) {
        const params = { netReturn, otherIncome, assessableAssets, isCouple, isHomeowner, includePension, 
                        adminFees, investmentFees, insuranceFees, adminFeePercentage, pensionGrowthRate, startingAge,
                        partnerStartingAge: partnerStartingAge ?? startingAge, memberShares };
        
        let balance = initialBalance;
        
        for (let year = 0; year < years; year++) {
            const result = this._simulateOneYearMonthly(balance, income, year, params);
            balance = result.finalBalance;
            
            // If balance goes negative, it's not sustainable
            if (balance <= 0) {
                return 0;
            }
        }
        
        // Return the actual final balance
        return balance;
    },

    // Helper function to get pension parameters (single source of truth) - using existing getAgePensionParams

    // SINGLE SOURCE OF TRUTH: Core Maximum Sustainable Income calculation
    // Handles pre-pension period logic and minimum withdrawal requirements
    calculateMaxSustainableIncomeCore(params) {
        const {
            totalSuper,
            returnsByYear,
            years,
            yourAge,
            otherAssetsValue = 0,
            otherIncome = 0,
            includePension = true,
            isCouple = false,
            isHomeowner = true,
            pensionGrowthRate = 0,
            adminFees = 52,
            adminFeePercentage = 0.10,
            investmentFees = 0,
            insuranceFees = 0,
            targetFinalBalance = 0
        } = params;
        
        console.log('🚀 Core MSI Calculation:', { 
            totalSuper, otherIncome, years, yourAge 
        });
        
        // Calculate fees function
        const calculateFees = (balance) => {
            // CRITICAL FIX: Use proper null checks to allow 0 values (0 is falsy but valid!)
            const adminFeesNum = (adminFees !== undefined && adminFees !== null) ? Number(adminFees) : 52;
            const adminFeePercentageNum = (adminFeePercentage !== undefined && adminFeePercentage !== null) ? Number(adminFeePercentage) : 0.10;
            const investmentFeesNum = Number(investmentFees) || 0;
            const insuranceFeesNum = Number(insuranceFees) || 0;
            
            let adminFeeAmount = adminFeesNum + (balance * adminFeePercentageNum / 100);
            if (adminFeesNum === 52 && adminFeePercentageNum === 0.10) {
                adminFeeAmount = Math.min(adminFeeAmount, 350);
            }
            
            const investmentFeeAmount = balance * (investmentFeesNum / 100);
            // REMOVED: No hardcoded transaction costs
            
            return adminFeeAmount + investmentFeeAmount + insuranceFeesNum;
        };
        
        // Get minimum withdrawal rate function
        const getMinimumWithdrawalRate = (age) => {
            if (age < 65) return 0.04;  // 4%
            if (age < 75) return 0.05;  // 5%
            if (age < 80) return 0.06;  // 6%
            if (age < 85) return 0.07;  // 7%
            if (age < 90) return 0.09;  // 9%
            if (age < 95) return 0.11;  // 11%
            return 0.14;  // 14%
        };
        
        // Calculate target pension based on final super balance after gap years
        const agePensionEligibilityAge = 67;
        const gapYears = Math.max(0, agePensionEligibilityAge - yourAge);
        
        // Estimate final super balance after minimum withdrawals during gap years
        let estimatedFinalSuper = totalSuper;
        for (let i = 0; i < gapYears; i++) {
            const { r } = returnsByYear[i] || { r: 0.07 };
            const currentAge = yourAge + i;
            
            // Apply minimum withdrawal during gap years
            const minWithdrawalRate = getMinimumWithdrawalRate(currentAge);
            const minWithdrawal = estimatedFinalSuper * minWithdrawalRate;
            
            estimatedFinalSuper -= minWithdrawal;
            estimatedFinalSuper *= (1 + r);
            estimatedFinalSuper -= calculateFees(estimatedFinalSuper);
        }
        
        // Calculate target pension
        const estimatedAssets = otherAssetsValue + Math.max(0, estimatedFinalSuper);
        const targetPension = includePension ? 
            (Number(SimulationEngine.calculatePension(estimatedAssets, otherIncome, isCouple, isHomeowner, gapYears, pensionGrowthRate)) || 0) : 0;
        
        const targetIncome = targetPension + otherIncome;
        
        // Binary search to find maximum sustainable income
        let low = Math.max(otherIncome, targetIncome * 0.5);
        let high = Math.max(targetIncome * 1.5, totalSuper * 0.15);
        
        let bestIncome = 0;
        const tolerance = 100;
        
        for (let iter = 0; iter < 50; iter++) {
            const mid = (low + high) / 2;
            
            // Test if this income is sustainable
            let testBal = totalSuper;
            let sustainable = true;
            
            for (let i = 0; i < years; i++) {
                const { r } = returnsByYear[i] || { r: 0.07 };
                const currentAge = yourAge + i;
                
                // Calculate pension
                let pensionAnnual = 0;
                if (includePension && currentAge >= agePensionEligibilityAge) {
                    const estimatedAssets = otherAssetsValue + Math.max(0, testBal);
                    pensionAnnual = Number(SimulationEngine.calculatePension(estimatedAssets, otherIncome, isCouple, isHomeowner, i, pensionGrowthRate)) || 0;
                }
                
                const totalOtherIncome = pensionAnnual + otherIncome;
                const needFromSuper = Math.max(0, mid - totalOtherIncome);
                
                // MSI CALCULATION: Only withdraw what's needed for constant income
                // Minimum withdrawal requirements will be applied in the simulation, not here
                const actualWithdrawal = Math.min(needFromSuper, testBal);
                
                // Apply fees and withdrawal
                testBal = Math.max(0, testBal - actualWithdrawal - calculateFees(testBal));
                
                // Check sustainability
                if (needFromSuper > 0 && actualWithdrawal < needFromSuper) {
                    sustainable = false;
                    break;
                }
            }
            
            if (sustainable) {
                bestIncome = mid;
                low = mid;
            } else {
                high = mid;
            }
            
            if (Math.abs(high - low) < tolerance) break;
        }
        
        const result = Math.round(bestIncome);
        console.log(`Final Core MSI result: $${result.toLocaleString()}`);
        return result;
    },

    // Legacy function - delegates to core MSI calculation
    calculateMaxSustainableIncomeAdvanced(totalSuper, returnsByYear, years, yourAge, otherAssetsValue, otherIncome, includePension, isCouple, isHomeowner, pensionGrowthRate, adminFees, adminFeePercentage, investmentFees, insuranceFees, strategy = 'fixed', targetFinalBalance = 0) {
        return SimulationEngine.calculateMaxSustainableIncomeCore({
            totalSuper,
            returnsByYear,
            years,
            yourAge,
            otherAssetsValue,
            otherIncome,
            includePension,
            isCouple,
            isHomeowner,
            pensionGrowthRate,
            adminFees,
            adminFeePercentage,
            investmentFees,
            insuranceFees,
            targetFinalBalance
        });
    }
};
